import axios from 'axios'
import { ossDirectUpload } from '@/utils/ossUpload'
import { normalizeRequestTimeFields, normalizeResponseTimeFields } from '@/utils/dateTime'
import { sanitizeApiError } from '@/utils/errorMessage'

// ============ Mock 模式配置 ============
// 设置为 true 时使用模拟数据，false 时请求真实后端
const USE_MOCK = false

// 用户角色常量
export const USER_ROLES = {
    ADMIN: 'ADMIN',     // 管理员
    TEACHER: 'TEACHER', // 老师
    STUDENT: 'STUDENT'  // 学生
}

// 模拟用户数据存储（使用localStorage持久化）
const getMockUsers = () => {
    const users = localStorage.getItem('mockUsers')
    if (users) return JSON.parse(users)

    // 初始化预设用户（用于测试）
    const defaultUsers = [
        { id: 1, email: 'admin@mooctest.com', password: '123456', realname: '系统管理员', role: USER_ROLES.ADMIN, teacherApproved: true, deleted: false, teacherMaxCapacity: 50, teacherMaxSubmissionFileCount: 10, teacherMaxSubmissionFileSize: 104857600, teacherAllowCreateProblem: null, teacherMuCoinCount: 0 },
        { id: 2, email: 'teacher@mooctest.com', password: '123456', realname: '张老师', role: USER_ROLES.TEACHER, teacherApproved: true, deleted: false, teacherMaxCapacity: 50, teacherMaxSubmissionFileCount: 10, teacherMaxSubmissionFileSize: 104857600, teacherAllowCreateProblem: false, teacherMuCoinCount: 0 },
        { id: 3, email: 'student@mooctest.com', password: '123456', realname: '李同学', role: USER_ROLES.STUDENT, teacherApproved: true, deleted: false, teacherMaxCapacity: 50, teacherMaxSubmissionFileCount: 10, teacherMaxSubmissionFileSize: 104857600, teacherAllowCreateProblem: null, teacherMuCoinCount: null }
    ]
    saveMockUsers(defaultUsers)
    return defaultUsers
}

const saveMockUsers = (users) => {
    localStorage.setItem('mockUsers', JSON.stringify(users))
}

const getMockMuCoinRecords = () => {
    const raw = localStorage.getItem('mockMuCoinRecords')
    if (raw) return JSON.parse(raw)
    const users = getMockUsers()
    const teacher = users.find(u => u.role === USER_ROLES.TEACHER)
    const now = new Date()
    const defaultRecords = teacher ? [
        {
            id: 1,
            teacherId: teacher.id,
            teacherUsername: teacher.realname || teacher.email,
            teacherIdentityNo: teacher.identityNo || String(teacher.id),
            teacherEmail: teacher.email,
            operatorId: teacher.id,
            operatorUsername: teacher.realname || teacher.email,
            recordType: 'CHARGE',
            scene: 'PROBLEM_GRADING',
            amount: -10,
            balanceAfter: Number(teacher.teacherMuCoinCount || 0),
            description: '题目判题扣费',
            sourceType: 'GRADING_JOB',
            sourceId: 1,
            businessInfo: {
                taskId: 1,
                taskName: '单元测试基础练习',
                classroomId: 1,
                classroomName: '软件测试2024春',
                problemId: 1,
                problemName: '计算器程序测试',
                problemNames: ['计算器程序测试'],
                problemCount: 1
            },
            createdAt: new Date(now.getTime() - 86400000).toISOString()
        },
        {
            id: 2,
            teacherId: teacher.id,
            teacherUsername: teacher.realname || teacher.email,
            teacherIdentityNo: teacher.identityNo || String(teacher.id),
            teacherEmail: teacher.email,
            operatorId: 1,
            operatorUsername: '系统管理员',
            recordType: 'ADMIN_ADJUST',
            scene: 'ADMIN_ADJUST',
            amount: 100,
            balanceAfter: Number(teacher.teacherMuCoinCount || 0) + 100,
            description: '管理员调整教师慕币余额',
            sourceType: 'ADMIN_USER_UPDATE',
            sourceId: teacher.id,
            businessInfo: null,
            createdAt: now.toISOString()
        }
    ] : []
    localStorage.setItem('mockMuCoinRecords', JSON.stringify(defaultRecords))
    return defaultRecords
}

const filterMockMuCoinRecords = (records, filters = {}) => {
    const teacherKeyword = String(filters.teacherKeyword || '').trim().toLowerCase()
    const recordType = String(filters.recordType || '').trim().toUpperCase()
    const scene = String(filters.scene || '').trim().toUpperCase()
    const teacherId = filters.teacherId === null || filters.teacherId === undefined || filters.teacherId === ''
        ? null
        : Number(filters.teacherId)
    const startTime = filters.startTime ? new Date(filters.startTime).getTime() : null
    const endTime = filters.endTime ? new Date(filters.endTime).getTime() + 24 * 60 * 60 * 1000 - 1 : null
    return records.filter(record => {
        if (teacherId !== null && Number(record.teacherId) !== teacherId) return false
        if (teacherKeyword) {
            const haystack = [
                record.teacherUsername,
                record.teacherIdentityNo,
                record.teacherEmail
            ].join(' ').toLowerCase()
            if (!haystack.includes(teacherKeyword)) return false
        }
        if (recordType && String(record.recordType || '').toUpperCase() !== recordType) return false
        if (scene && String(record.scene || '').toUpperCase() !== scene) return false
        const created = new Date(record.createdAt).getTime()
        if (startTime && created < startTime) return false
        if (endTime && created > endTime) return false
        return true
    })
}

const pageMockMuCoinRecords = (records, page = 0, size = 20) => {
    const safePage = Math.max(Number(page) || 0, 0)
    const safeSize = Math.max(Number(size) || 20, 1)
    const sorted = [...records].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    const start = safePage * safeSize
    const items = sorted.slice(start, start + safeSize)
    return {
        items,
        page: safePage,
        size: safeSize,
        total: sorted.length,
        totalPages: Math.ceil(sorted.length / safeSize)
    }
}

const getTeacherMaxCapacity = (teacherId) => {
    const users = getMockUsers()
    const teacher = users.find(u => u.id === teacherId)
    const limit = teacher?.teacherMaxCapacity
    return Number(limit) > 0 ? Number(limit) : 50
}

const formatLocalDateOnly = (value = new Date()) => {
    const date = value instanceof Date ? value : new Date(value)
    if (Number.isNaN(date.getTime())) return ''
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

// ============ Mock 班级数据存储 ============
const getMockClasses = () => {
    const classes = localStorage.getItem('mockClasses')
    if (classes) return JSON.parse(classes)

    // 初始化预设班级（用于测试）
    const defaultClasses = [
        { id: 1, name: '软件测试2024春', code: 'CS-TEST-2401', description: '软件测试课程', joinDeadline: '2024-02-28 23:59', teacherId: 2, teacherName: '张老师', studentCount: 126, taskCount: 8, semester: '2024年春季学期', status: 'active', allowJoin: true, createdAt: '2024-02-01', capacity: 120, maxCapacity: 160, disabled: false, deleted: false },
        { id: 2, name: '软件工程2024春', code: 'CS-SE-2401', description: '软件工程课程', joinDeadline: '2024-03-05 23:59', teacherId: 2, teacherName: '张老师', studentCount: 98, taskCount: 5, semester: '2024年春季学期', status: 'active', allowJoin: true, createdAt: '2024-02-01', capacity: 100, maxCapacity: 140, disabled: false, deleted: false },
        { id: 3, name: '计算机基础2024春', code: 'CS-BASE-2401', description: '计算机基础课程', joinDeadline: null, teacherId: 2, teacherName: '张老师', studentCount: 156, taskCount: 6, semester: '2024年春季学期', status: 'active', allowJoin: false, createdAt: '2024-02-01', capacity: 150, maxCapacity: 180, disabled: false, deleted: false }
    ]
    saveMockClasses(defaultClasses)
    return defaultClasses
}

const saveMockClasses = (classes) => {
    localStorage.setItem('mockClasses', JSON.stringify(classes))
}

// Mock 任务数据存储
const getMockTasks = () => {
    const tasks = localStorage.getItem('mockTasks')
    if (tasks) return JSON.parse(tasks)

    const defaultTasks = [
        {
            id: 1,
            name: '单元测试基础练习',
            description: '单元测试基础练习任务',
            classroomId: 1,
            creatorId: 2,
            taskType: 'DEVELOPER_TEST',
            startTime: '2024-01-15T00:00:00',
            endTime: '2024-01-25T23:59:59',
            problemCount: 6,
            submissionRate: 0.78,
            averageScore: 85.6,
            createdAt: '2024-01-10T10:00:00',
            updatedAt: '2024-01-12T10:00:00'
        },
        {
            id: 2,
            name: 'Web自动化测试实践',
            description: 'Web自动化测试实践任务',
            classroomId: 1,
            creatorId: 2,
            taskType: 'WEB_TEST',
            startTime: '2024-01-20T00:00:00',
            endTime: '2024-01-30T23:59:59',
            problemCount: 4,
            submissionRate: 0.62,
            averageScore: 82.3,
            createdAt: '2024-01-12T09:30:00',
            updatedAt: '2024-01-13T09:30:00'
        },
        {
            id: 3,
            name: '嵌入式系统测试项目',
            description: '嵌入式系统测试项目',
            classroomId: 2,
            creatorId: 2,
            taskType: 'EMBEDDED_TEST',
            startTime: '2024-01-01T00:00:00',
            endTime: '2024-01-10T23:59:59',
            problemCount: 5,
            submissionRate: 0.95,
            averageScore: 88.5,
            createdAt: '2023-12-25T08:00:00',
            updatedAt: '2023-12-28T08:00:00'
        },
        {
            id: 4,
            name: '开发者测试进阶训练',
            description: '开发者测试进阶训练',
            classroomId: 3,
            creatorId: 2,
            taskType: 'DEVELOPER_TEST',
            startTime: '2024-01-26T00:00:00',
            endTime: '2024-02-05T23:59:59',
            problemCount: 7,
            submissionRate: 0,
            averageScore: null,
            createdAt: '2024-01-18T10:00:00',
            updatedAt: '2024-01-20T10:00:00'
        }
    ]
    saveMockTasks(defaultTasks)
    return defaultTasks
}

const saveMockTasks = (tasks) => {
    localStorage.setItem('mockTasks', JSON.stringify(tasks))
}

// Mock 测试大赛数据存储
const getMockContests = () => {
    const contests = localStorage.getItem('mockContests')
    if (contests) return JSON.parse(contests)

    const defaultContests = [
        {
            id: 1,
            title: '2026年春季全国大学生单元测试大赛',
            description: '面向全国高校的单元测试专项挑战，提升测试覆盖率与质量意识。',
            type: 'DEVELOPER_TEST',
            level: 'NATIONAL',
            registrationStart: '2026-01-15T00:00:00',
            registrationEnd: '2026-02-28T23:59:00',
            contestStart: '2026-03-01T00:00:00',
            contestEnd: '2026-03-15T23:59:00',
            officialUrl: 'https://www.mooctest.org',
            detailUrl: 'https://www.mooctest.org',
            rankingUrl: 'https://www.mooctest.org',
            resultUrl: 'https://www.mooctest.org',
            problemCount: 5,
            resultPublished: false
        },
        {
            id: 2,
            title: '2026年寒假Web自动化测试挑战赛',
            description: '聚焦Web自动化测试实践，完成真实业务场景的脚本编写。',
            type: 'WEB_TEST',
            level: 'PROVINCIAL',
            registrationStart: '2025-12-20T00:00:00',
            registrationEnd: '2026-01-18T23:59:00',
            contestStart: '2026-01-20T00:00:00',
            contestEnd: '2026-02-10T23:59:00',
            officialUrl: 'https://www.mooctest.org',
            detailUrl: 'https://www.mooctest.org',
            rankingUrl: 'https://www.mooctest.org',
            resultUrl: 'https://www.mooctest.org',
            problemCount: 5,
            resultPublished: false
        },
        {
            id: 3,
            title: '2026年移动应用测试精英赛',
            description: '移动端专项测试赛，涵盖兼容性、性能与稳定性评估。',
            type: 'EMBEDDED_TEST',
            level: 'NATIONAL',
            registrationStart: '2026-02-01T00:00:00',
            registrationEnd: '2026-03-20T23:59:00',
            contestStart: '2026-03-25T00:00:00',
            contestEnd: '2026-04-10T23:59:00',
            officialUrl: 'https://www.mooctest.org',
            detailUrl: 'https://www.mooctest.org',
            rankingUrl: 'https://www.mooctest.org',
            resultUrl: 'https://www.mooctest.org',
            problemCount: 6,
            resultPublished: false
        },
        {
            id: 4,
            title: '2025年冬季性能测试大赛',
            description: '通过压测与瓶颈分析提升系统性能调优能力。',
            type: 'PERFORMANCE_TEST',
            level: 'SCHOOL',
            registrationStart: '2025-11-01T00:00:00',
            registrationEnd: '2025-11-25T23:59:00',
            contestStart: '2025-12-01T00:00:00',
            contestEnd: '2025-12-20T23:59:00',
            officialUrl: 'https://www.mooctest.org',
            detailUrl: 'https://www.mooctest.org',
            rankingUrl: 'https://www.mooctest.org',
            resultUrl: 'https://www.mooctest.org',
            problemCount: 4,
            resultPublished: true
        }
    ]
    saveMockContests(defaultContests)
    return defaultContests
}

const saveMockContests = (contests) => {
    localStorage.setItem('mockContests', JSON.stringify(contests))
}

const getMockContestParticipations = () => {
    const raw = localStorage.getItem('mockContestParticipations')
    if (raw) return JSON.parse(raw)
    const defaults = []
    saveMockContestParticipations(defaults)
    return defaults
}

const saveMockContestParticipations = (participations) => {
    localStorage.setItem('mockContestParticipations', JSON.stringify(participations))
}

// Mock 题目数据存储
const getMockProblems = () => {
    const problems = localStorage.getItem('mockProblems')
    if (problems) return JSON.parse(problems)

    const defaultProblems = [
        {
            id: 1,
            name: '计算器程序测试',
            type: 'DEVELOPER_TEST',
            difficulty: 'EASY',
            status: 'ENABLED',
            isPublic: true,
            creatorId: 2,
            classroomId: 1,
            usageCount: 58,
            fileCount: 3,
            totalFileSize: 2420000,
            allowedExtensions: 'zip,java,py',
            submissionDescription: '请提交源代码与说明文档',
            submissionRules: [
                { description: '提交文件', allowedExtensions: 'zip,java,py', maxFileSize: 10485760 }
            ],
            createdAt: '2024-01-10T10:00:00',
            updatedAt: '2024-02-10T09:30:00'
        },
        {
            id: 2,
            name: '移动App自动化测试',
            type: 'WEB_TEST',
            difficulty: 'MEDIUM',
            status: 'DISABLED',
            isPublic: false,
            creatorId: 2,
            classroomId: 2,
            usageCount: 0,
            fileCount: 1,
            totalFileSize: 860000,
            allowedExtensions: 'zip',
            submissionDescription: '提交测试脚本与报告',
            submissionRules: [
                { description: '提交文件', allowedExtensions: 'zip', maxFileSize: 10485760 }
            ],
            createdAt: '2024-02-20T10:00:00',
            updatedAt: '2024-03-01T12:00:00'
        },
        {
            id: 3,
            name: '在线购物系统测试',
            type: 'WEB_TEST',
            difficulty: 'MEDIUM',
            status: 'ENABLED',
            isPublic: true,
            creatorId: 2,
            classroomId: null,
            usageCount: 24,
            fileCount: 4,
            totalFileSize: 1560000,
            allowedExtensions: 'zip,sql',
            submissionDescription: '请提交测试报告',
            submissionRules: [
                { description: '提交文件', allowedExtensions: 'zip,sql', maxFileSize: 10485760 }
            ],
            createdAt: '2024-01-05T08:30:00',
            updatedAt: '2024-01-15T15:00:00'
        },
        {
            id: 4,
            name: '分布式微服务架构测试',
            type: 'EMBEDDED_TEST',
            difficulty: 'HARD',
            status: 'ENABLED',
            isPublic: false,
            creatorId: 2,
            classroomId: 3,
            usageCount: 12,
            fileCount: 2,
            totalFileSize: 432000,
            allowedExtensions: 'zip,md',
            submissionDescription: '提交测试用例与报告',
            submissionRules: [
                { description: '提交文件', allowedExtensions: 'zip,md', maxFileSize: 10485760 }
            ],
            createdAt: '2023-12-01T08:00:00',
            updatedAt: '2023-12-05T11:00:00'
        }
    ]
    saveMockProblems(defaultProblems)
    return defaultProblems
}

const saveMockProblems = (problems) => {
    localStorage.setItem('mockProblems', JSON.stringify(problems))
}

// Mock 学生班级关系存储
const getMockStudentClasses = () => {
    const data = localStorage.getItem('mockStudentClasses')
    if (data) return JSON.parse(data)
    // 初始化：学生3加入了班级1和2
    const defaultData = [
        { studentId: 3, classId: 1, joinedAt: '2024-02-15' },
        { studentId: 3, classId: 2, joinedAt: '2024-02-16' }
    ]
    saveMockStudentClasses(defaultData)
    return defaultData
}

const saveMockStudentClasses = (data) => {
    localStorage.setItem('mockStudentClasses', JSON.stringify(data))
}

// Mock 学生导入模板存储
const getMockStudentTemplate = () => {
    const raw = localStorage.getItem('mockStudentImportTemplate')
    return raw ? JSON.parse(raw) : null
}

const saveMockStudentTemplate = (template) => {
    localStorage.setItem('mockStudentImportTemplate', JSON.stringify(template))
}

// Mock 教师使用证明模板存储
const getMockTeacherCertTemplate = () => {
    const raw = localStorage.getItem('mockTeacherCertTemplate')
    return raw ? JSON.parse(raw) : null
}

const saveMockTeacherCertTemplate = (template) => {
    localStorage.setItem('mockTeacherCertTemplate', JSON.stringify(template))
}

// Mock 系统配置存储
const getMockSystemSettings = () => {
    const raw = localStorage.getItem('mockSystemSettings')
    if (raw) return JSON.parse(raw)
    const defaults = {
        uploadMaxFileSize: 104857600,
        uploadMaxRequestSize: 209715200,
        contestEnabled: false,
        gradingChargeEasy: 5,
        gradingChargeMedium: 5,
        gradingChargeHard: 5,
        reportChargeMode: 'PERSON_AND_DIFFICULTY',
        reportChargeEasy: 0,
        reportChargeMedium: 0,
        reportChargeHard: 0,
        reportChargeClass: 0,
        classReportChargeMode: 'PERSON_AND_DIFFICULTY',
        classReportChargeEasy: 0,
        classReportChargeMedium: 0,
        classReportChargeHard: 0,
        classReportChargeClass: 0
    }
    saveMockSystemSettings(defaults)
    return defaults
}

const saveMockSystemSettings = (settings) => {
    localStorage.setItem('mockSystemSettings', JSON.stringify(settings))
}

const dataUrlToBlob = (dataUrl, fallbackType = 'application/octet-stream') => {
    if (!dataUrl) return new Blob([], { type: fallbackType })
    const [meta, base64] = dataUrl.split(',')
    const contentType = meta?.match(/data:(.*?);base64/)?.[1] || fallbackType
    const binary = atob(base64 || '')
    const len = binary.length
    const bytes = new Uint8Array(len)
    for (let i = 0; i < len; i += 1) {
        bytes[i] = binary.charCodeAt(i)
    }
    return new Blob([bytes], { type: contentType })
}

// 获取当前登录用户
const getCurrentUser = () => {
    const userStr = localStorage.getItem('currentUser')
    return userStr ? JSON.parse(userStr) : null
}

const saveCurrentUser = (user) => {
    localStorage.setItem('currentUser', JSON.stringify(user))
}

const getMockUserStatus = (user) => {
    if (!user) return 'inactive'
    if (user.deleted || user.disabled) return 'inactive'
    if (user.role === USER_ROLES.TEACHER && user.teacherApproved === false) return 'pending'
    return 'active'
}

const generateMockIdentityNo = (users, role) => {
    const prefix = role === USER_ROLES.TEACHER ? 'T' : 'S'
    let candidate = ''
    do {
        const digits = String(Math.floor(Math.random() * 1_000_000_0000)).padStart(10, '0')
        candidate = `${prefix}${digits}`
    } while (users.some(u => u.identityNo === candidate))
    return candidate
}

// Mock API 响应
const mockApi = {
    // 模拟登录
    login: (data) => {
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                const users = getMockUsers()
                const user = users.find(u => u.email === data.email && u.password === data.password)

                if (user) {
                    const status = getMockUserStatus(user)
                    if (status === 'inactive') {
                        reject({
                            response: {
                                status: 403,
                                data: { message: '账号已被禁用，请联系管理员。' }
                            }
                        })
                        return
                    }
                    if (status === 'pending') {
                        reject({
                            response: {
                                status: 403,
                                data: { message: '教师账号待管理员审核通过后方可登录。' }
                            }
                        })
                        return
                    }
                    const userData = {
                        id: user.id,
                        email: user.email,
                        realname: user.realname,
                        role: user.role
                    }
                    saveCurrentUser(userData)

                    resolve({
                        code: 200,
                        message: '登录成功',
                        data: {
                            token: 'mock_token_' + Date.now(),
                            user: userData
                        }
                    })
                } else {
                    reject({
                        response: {
                            status: 401,
                            data: { message: '邮箱或密码错误' }
                        }
                    })
                }
            }, 500)
        })
    },

    // 模拟注册（默认注册为学生）
    register: (data) => {
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                const users = getMockUsers()

                if (users.find(u => u.email === data.email)) {
                    reject({
                        response: {
                            status: 400,
                            data: { message: '该邮箱已被注册' }
                        }
                    })
                    return
                }

                const role = data.role || USER_ROLES.STUDENT
                const newUser = {
                    id: users.length + 1,
                    email: data.email,
                    realname: data.realname,
                    password: data.password,
                    role,
                    identityNo: generateMockIdentityNo(users, role),
                    teacherApproved: role !== USER_ROLES.TEACHER,
                    deleted: false,
                    teacherMuCoinCount: role === USER_ROLES.TEACHER ? 0 : null,
                    teacherAllowCreateProblem: role === USER_ROLES.TEACHER ? false : null,
                    createdAt: new Date().toISOString()
                }
                users.push(newUser)
                saveMockUsers(users)

                console.log('✅ Mock注册成功，当前用户列表：', users)

                resolve({
                    code: 200,
                    message: '注册成功',
                    data: {
                        id: newUser.id,
                        email: newUser.email,
                        realname: newUser.realname,
                        role: newUser.role
                    }
                })
            }, 500)
        })
    },

    // 获取用户信息
    getUserProfile: (userId) => {
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                const users = getMockUsers()
                const user = users.find(u => u.id === userId)

                if (user) {
                    resolve({
                        code: 200,
                        data: {
                            id: user.id,
                            email: user.email,
                            username: user.realname,
                            role: user.role,
                            phone: null,
                            wechat: null,
                            github: null,
                            gitcode: null,
                            companyName: null,
                            address: null,
                            teacherMaxCapacity: user.teacherMaxCapacity || 50,
                            teacherMaxSubmissionFileCount: user.teacherMaxSubmissionFileCount || 10,
                            teacherMaxSubmissionFileSize: user.teacherMaxSubmissionFileSize || 104857600,
                            teacherAllowCreateProblem: Boolean(user.teacherAllowCreateProblem),
                            teacherMuCoinCount: user.role === USER_ROLES.TEACHER ? Number(user.teacherMuCoinCount || 0) : null
                        }
                    })
                } else {
                    reject({
                        response: {
                            status: 404,
                            data: { message: '用户不存在' }
                        }
                    })
                }
            }, 300)
        })
    },

    // 获取当前用户信息
    getCurrentUserInfo: () => {
        return new Promise((resolve) => {
            setTimeout(() => {
                const user = getCurrentUser()
                if (user) {
                    resolve({ code: 200, data: user })
                } else {
                    resolve({ code: 401, data: null })
                }
            }, 200)
        })
    },

    // ============ 班级管理 Mock API ============
    // 创建班级
    createClass: (teacherId, data) => {
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                const classes = getMockClasses()
                const currentUser = getCurrentUser()
                const capacity = data.capacity || 50
                const teacherMaxCapacity = getTeacherMaxCapacity(teacherId)
                if (capacity > teacherMaxCapacity) {
                    reject({ response: { status: 400, data: { message: `班级容量不能超过班级最大容量(${teacherMaxCapacity})` } } })
                    return
                }
                const newClass = {
                    id: classes.length + 1,
                    name: data.name || data.className,
                    code: 'CLS-' + Date.now().toString().slice(-6),
                    description: data.description || '',
                    joinDeadline: data.joinDeadline || null,
                    teacherId: teacherId,
                    teacherName: currentUser?.realname || '未知教师',
                    studentCount: 0,
                    taskCount: 0,
                    capacity: capacity,
                    maxCapacity: teacherMaxCapacity,
                    disabled: false,
                    deleted: false,
                    semester: new Date().getFullYear() + '年' + (new Date().getMonth() < 6 ? '春季' : '秋季') + '学期',
                    status: 'active',
                    allowJoin: data.allowJoin !== false,
                    createdAt: formatLocalDateOnly()
                }
                classes.push(newClass)
                saveMockClasses(classes)
                console.log('✅ Mock创建班级成功：', newClass)
                resolve({ code: 200, message: '创建成功', data: newClass })
            }, 500)
        })
    },

    // 获取教师班级列表
    getTeacherClasses: (teacherId, page = 0, size = 20, search = '') => {
        return new Promise((resolve) => {
            setTimeout(() => {
                const classes = getMockClasses()
                const keyword = String(search || '').trim().toLowerCase()
                const teacherClasses = classes.filter(c => {
                    if (c.teacherId !== teacherId || c.deleted) return false
                    if (!keyword) return true
                    return [c.name, c.code].some(value => String(value || '').toLowerCase().includes(keyword))
                })
                const total = teacherClasses.length
                const safePage = Math.max(0, Number(page) || 0)
                const safeSize = Math.max(1, Number(size) || 20)
                const start = safePage * safeSize
                const end = start + safeSize
                const list = teacherClasses.slice(start, end)
                const totalPages = total > 0 ? Math.ceil(total / safeSize) : 0
                resolve({
                    code: 200,
                    data: {
                        items: list,
                        list,
                        totalElements: total,
                        total,
                        page: safePage,
                        size: safeSize,
                        totalPages
                    }
                })
            }, 300)
        })
    },
    // 获取教师班级详情
    getTeacherClassDetail: (teacherId, classId) => {
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                const classes = getMockClasses()
                const target = classes.find(c => c.id === classId && !c.deleted)
                if (!target || target.teacherId !== teacherId) {
                    reject({ response: { status: 404, data: { message: '班级不存在' } } })
                    return
                }
                resolve({ code: 200, data: target })
            }, 300)
        })
    },
    // 更新班级
    updateClass: (teacherId, classId, data) => {
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                const classes = getMockClasses()
                const target = classes.find(c => c.id === classId && !c.deleted)
                if (!target || target.teacherId !== teacherId) {
                    reject({ response: { status: 404, data: { message: '班级不存在' } } })
                    return
                }
                if (data.name) target.name = data.name
                if (data.joinDeadline !== undefined) {
                    target.joinDeadline = data.joinDeadline
                }
                if (data.description !== undefined) {
                    target.description = data.description
                }
                if (data.capacity) {
                    if (data.capacity > (target.maxCapacity || target.capacity)) {
                        reject({ response: { status: 400, data: { message: `班级容量不能超过班级最大容量(${target.maxCapacity || target.capacity})` } } })
                        return
                    }
                    if (data.capacity < target.studentCount) {
                        reject({ response: { status: 400, data: { message: '容量不能小于当前班级人数' } } })
                        return
                    }
                    target.capacity = data.capacity
                }
                if (data.disabled !== undefined) {
                    target.disabled = data.disabled
                }
                saveMockClasses(classes)
                resolve({ code: 200, message: '更新成功', data: target })
            }, 300)
        })
    },
    // 删除班级（逻辑删除）
    deleteClass: (teacherId, classId) => {
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                const classes = getMockClasses()
                const target = classes.find(c => c.id === classId && !c.deleted)
                if (!target || target.teacherId !== teacherId) {
                    reject({ response: { status: 404, data: { message: '班级不存在' } } })
                    return
                }
                target.deleted = true
                saveMockClasses(classes)
                resolve({ code: 200, message: '删除成功', data: null })
            }, 300)
        })
    },
    updateMaxCapacity: (teacherId, classId, maxCapacity) => {
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                const classes = getMockClasses()
                const target = classes.find(c => c.id === classId && !c.deleted)
                if (!target) {
                    reject({ response: { status: 404, data: { message: '班级不存在' } } })
                    return
                }
                if (maxCapacity < target.studentCount) {
                    reject({ response: { status: 400, data: { message: '容量上限不能小于当前班级人数' } } })
                    return
                }
                if (maxCapacity < target.capacity) {
                    reject({ response: { status: 400, data: { message: '容量上限不能小于当前班级容量' } } })
                    return
                }
                target.maxCapacity = maxCapacity
                saveMockClasses(classes)
                resolve({ code: 200, message: '更新成功', data: target })
            }, 300)
        })
    },
    updateTeacherMaxCapacity: (teacherId, maxCapacity) => {
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                const users = getMockUsers()
                const teacher = users.find(u => u.id === teacherId)
                if (!teacher) {
                    reject({ response: { status: 404, data: { message: '用户不存在' } } })
                    return
                }
                if (Number(maxCapacity) <= 0) {
                    reject({ response: { status: 400, data: { message: '容量上限必须大于0' } } })
                    return
                }
                const classes = getMockClasses()
                const teacherClasses = classes.filter(c => c.teacherId === teacherId && !c.deleted)
                const overLimit = teacherClasses.find(c => c.capacity > maxCapacity)
                if (overLimit) {
                    reject({ response: { status: 400, data: { message: `容量上限不能小于教师已有班级容量(${overLimit.capacity})` } } })
                    return
                }
                teacher.teacherMaxCapacity = Number(maxCapacity)
                teacherClasses.forEach(c => {
                    c.maxCapacity = Number(maxCapacity)
                })
                saveMockUsers(users)
                saveMockClasses(classes)
                resolve({ code: 200, message: '更新成功', data: teacher })
            }, 300)
        })
    },
    updateTeacherSubmissionLimits: (teacherId, maxFileCount, maxFileSize, allowCreateProblem) => {
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                const users = getMockUsers()
                const teacher = users.find(u => u.id === teacherId)
                if (!teacher) {
                    reject({ response: { status: 404, data: { message: '用户不存在' } } })
                    return
                }
                if (!Number.isFinite(Number(maxFileCount)) || Number(maxFileCount) <= 0) {
                    reject({ response: { status: 400, data: { message: '文件数量上限必须大于0' } } })
                    return
                }
                if (!Number.isFinite(Number(maxFileSize)) || Number(maxFileSize) <= 0) {
                    reject({ response: { status: 400, data: { message: '文件大小上限必须大于0' } } })
                    return
                }
                if (typeof allowCreateProblem !== 'boolean') {
                    reject({ response: { status: 400, data: { message: '创建题目权限参数无效' } } })
                    return
                }
                teacher.teacherMaxSubmissionFileCount = Number(maxFileCount)
                teacher.teacherMaxSubmissionFileSize = Number(maxFileSize)
                teacher.teacherAllowCreateProblem = allowCreateProblem
                saveMockUsers(users)
                resolve({ code: 200, message: '更新成功', data: teacher })
            }, 300)
        })
    },
    getAdminUploadLimits: () => {
        return new Promise((resolve) => {
            setTimeout(() => {
                const settings = getMockSystemSettings()
                resolve({
                    code: 200,
                    message: '获取成功',
                    data: {
                        maxFileSize: settings.uploadMaxFileSize,
                        maxRequestSize: settings.uploadMaxRequestSize
                    }
                })
            }, 200)
        })
    },
    getAdminProblemGradingChargeConfig: () => {
        return new Promise((resolve) => {
            setTimeout(() => {
                const settings = getMockSystemSettings()
                resolve({
                    code: 200,
                    message: '获取成功',
                    data: {
                        easyMuCoinPerPerson: Number(settings.gradingChargeEasy ?? 5),
                        mediumMuCoinPerPerson: Number(settings.gradingChargeMedium ?? 5),
                        hardMuCoinPerPerson: Number(settings.gradingChargeHard ?? 5)
                    }
                })
            }, 200)
        })
    },
    getAdminTaskReportChargeConfig: () => {
        return new Promise((resolve) => {
            setTimeout(() => {
                const settings = getMockSystemSettings()
                resolve({
                    code: 200,
                    message: '获取成功',
                    data: {
                        chargeMode: settings.reportChargeMode || 'PERSON_AND_DIFFICULTY',
                        easyMuCoinPerPerson: Number(settings.reportChargeEasy ?? 0),
                        mediumMuCoinPerPerson: Number(settings.reportChargeMedium ?? 0),
                        hardMuCoinPerPerson: Number(settings.reportChargeHard ?? 0),
                        classMuCoinCost: Number(settings.reportChargeClass ?? 0)
                    }
                })
            }, 200)
        })
    },
    getAdminTaskClassReportChargeConfig: () => {
        return new Promise((resolve) => {
            setTimeout(() => {
                const settings = getMockSystemSettings()
                resolve({
                    code: 200,
                    message: '获取成功',
                    data: {
                        chargeMode: settings.classReportChargeMode || 'PERSON_AND_DIFFICULTY',
                        easyMuCoinPerPerson: Number(settings.classReportChargeEasy ?? 0),
                        mediumMuCoinPerPerson: Number(settings.classReportChargeMedium ?? 0),
                        hardMuCoinPerPerson: Number(settings.classReportChargeHard ?? 0),
                        classMuCoinCost: Number(settings.classReportChargeClass ?? 0)
                    }
                })
            }, 200)
        })
    },
    updateAdminUploadLimits: (maxFileSize, maxRequestSize) => {
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                if (!Number.isFinite(Number(maxFileSize)) || Number(maxFileSize) <= 0) {
                    reject({ response: { status: 400, data: { message: '单文件大小上限必须大于0' } } })
                    return
                }
                if (!Number.isFinite(Number(maxRequestSize)) || Number(maxRequestSize) <= 0) {
                    reject({ response: { status: 400, data: { message: '总上传大小上限必须大于0' } } })
                    return
                }
                if (Number(maxRequestSize) < Number(maxFileSize)) {
                    reject({ response: { status: 400, data: { message: '总上传大小上限不能小于单文件上限' } } })
                    return
                }
                const settings = {
                    ...getMockSystemSettings(),
                    uploadMaxFileSize: Number(maxFileSize),
                    uploadMaxRequestSize: Number(maxRequestSize)
                }
                saveMockSystemSettings(settings)
                resolve({
                    code: 200,
                    message: '更新成功',
                    data: {
                        maxFileSize: settings.uploadMaxFileSize,
                        maxRequestSize: settings.uploadMaxRequestSize
                    }
                })
            }, 300)
        })
    },
    updateAdminProblemGradingChargeConfig: (easyMuCoinPerPerson, mediumMuCoinPerPerson, hardMuCoinPerPerson) => {
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                const easy = Number(easyMuCoinPerPerson)
                const medium = Number(mediumMuCoinPerPerson)
                const hard = Number(hardMuCoinPerPerson)
                if (!Number.isInteger(easy) || easy < 0) {
                    reject({ response: { status: 400, data: { message: '简单题每人慕币数不能小于0' } } })
                    return
                }
                if (!Number.isInteger(medium) || medium < 0) {
                    reject({ response: { status: 400, data: { message: '中等题每人慕币数不能小于0' } } })
                    return
                }
                if (!Number.isInteger(hard) || hard < 0) {
                    reject({ response: { status: 400, data: { message: '困难题每人慕币数不能小于0' } } })
                    return
                }
                const settings = {
                    ...getMockSystemSettings(),
                    gradingChargeEasy: easy,
                    gradingChargeMedium: medium,
                    gradingChargeHard: hard
                }
                saveMockSystemSettings(settings)
                resolve({
                    code: 200,
                    message: '更新成功',
                    data: {
                        easyMuCoinPerPerson: easy,
                        mediumMuCoinPerPerson: medium,
                        hardMuCoinPerPerson: hard
                    }
                })
            }, 300)
        })
    },
    updateAdminTaskReportChargeConfig: (chargeMode, easyMuCoinPerPerson, mediumMuCoinPerPerson, hardMuCoinPerPerson, classMuCoinCost) => {
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                const mode = String(chargeMode || '').trim().toUpperCase()
                const easy = Number(easyMuCoinPerPerson)
                const medium = Number(mediumMuCoinPerPerson)
                const hard = Number(hardMuCoinPerPerson)
                const classCost = Number(classMuCoinCost)
                if (!['PERSON_AND_DIFFICULTY', 'CLASS_FLAT'].includes(mode)) {
                    reject({ response: { status: 400, data: { message: '报告生成计费方式不正确' } } })
                    return
                }
                if (!Number.isInteger(easy) || easy < 0) {
                    reject({ response: { status: 400, data: { message: '简单题每人慕币数不能小于0' } } })
                    return
                }
                if (!Number.isInteger(medium) || medium < 0) {
                    reject({ response: { status: 400, data: { message: '中等题每人慕币数不能小于0' } } })
                    return
                }
                if (!Number.isInteger(hard) || hard < 0) {
                    reject({ response: { status: 400, data: { message: '困难题每人慕币数不能小于0' } } })
                    return
                }
                if (!Number.isInteger(classCost) || classCost < 0) {
                    reject({ response: { status: 400, data: { message: '班级整体慕币数不能小于0' } } })
                    return
                }
                const settings = {
                    ...getMockSystemSettings(),
                    reportChargeMode: mode,
                    reportChargeEasy: easy,
                    reportChargeMedium: medium,
                    reportChargeHard: hard,
                    reportChargeClass: classCost
                }
                saveMockSystemSettings(settings)
                resolve({
                    code: 200,
                    message: '更新成功',
                    data: {
                        chargeMode: mode,
                        easyMuCoinPerPerson: easy,
                        mediumMuCoinPerPerson: medium,
                        hardMuCoinPerPerson: hard,
                        classMuCoinCost: classCost
                    }
                })
            }, 300)
        })
    },
    updateAdminTaskClassReportChargeConfig: (chargeMode, easyMuCoinPerPerson, mediumMuCoinPerPerson, hardMuCoinPerPerson, classMuCoinCost) => {
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                const mode = String(chargeMode || '').trim().toUpperCase()
                const easy = Number(easyMuCoinPerPerson)
                const medium = Number(mediumMuCoinPerPerson)
                const hard = Number(hardMuCoinPerPerson)
                const classCost = Number(classMuCoinCost)
                if (!['PERSON_AND_DIFFICULTY', 'CLASS_FLAT'].includes(mode)) {
                    reject({ response: { status: 400, data: { message: '班级报告计费方式不正确' } } })
                    return
                }
                if (!Number.isInteger(easy) || easy < 0) {
                    reject({ response: { status: 400, data: { message: '简单题每人慕币数不能小于0' } } })
                    return
                }
                if (!Number.isInteger(medium) || medium < 0) {
                    reject({ response: { status: 400, data: { message: '中等题每人慕币数不能小于0' } } })
                    return
                }
                if (!Number.isInteger(hard) || hard < 0) {
                    reject({ response: { status: 400, data: { message: '困难题每人慕币数不能小于0' } } })
                    return
                }
                if (!Number.isInteger(classCost) || classCost < 0) {
                    reject({ response: { status: 400, data: { message: '班级整体慕币数不能小于0' } } })
                    return
                }
                const settings = {
                    ...getMockSystemSettings(),
                    classReportChargeMode: mode,
                    classReportChargeEasy: easy,
                    classReportChargeMedium: medium,
                    classReportChargeHard: hard,
                    classReportChargeClass: classCost
                }
                saveMockSystemSettings(settings)
                resolve({
                    code: 200,
                    message: '更新成功',
                    data: {
                        chargeMode: mode,
                        easyMuCoinPerPerson: easy,
                        mediumMuCoinPerPerson: medium,
                        hardMuCoinPerPerson: hard,
                        classMuCoinCost: classCost
                    }
                })
            }, 300)
        })
    },
    getTaskReportChargePreview: (taskId) => {
        return new Promise((resolve) => {
            setTimeout(() => {
                const settings = getMockSystemSettings()
                const currentUser = getCurrentUser()
                const users = getMockUsers()
                const teacher = users.find(item => item.id === currentUser?.id)
                const chargeMode = settings.reportChargeMode || 'PERSON_AND_DIFFICULTY'
                const submittedCount = 1
                const reportableProblemCount = 1
                const easyProblemCount = 1
                const mediumProblemCount = 0
                const hardProblemCount = 0
                const perPersonMuCoinCost = Number(settings.reportChargeEasy ?? 0)
                const classMuCoinCost = Number(settings.reportChargeClass ?? 0)
                const totalMuCoinCost = chargeMode === 'CLASS_FLAT'
                    ? classMuCoinCost
                    : perPersonMuCoinCost * submittedCount
                const currentMuCoinCount = Number(teacher?.teacherMuCoinCount ?? 0)
                resolve({
                    code: 200,
                    message: '获取成功',
                    data: {
                        taskId: Number(taskId),
                        chargeMode,
                        submittedCount,
                        reportableProblemCount,
                        easyProblemCount,
                        mediumProblemCount,
                        hardProblemCount,
                        perPersonMuCoinCost,
                        classMuCoinCost,
                        totalMuCoinCost,
                        chargeRequired: true,
                        currentMuCoinCount,
                        enough: currentMuCoinCount >= totalMuCoinCost
                    }
                })
            }, 200)
        })
    },
    getTaskClassReportChargePreview: (taskId) => {
        return new Promise((resolve) => {
            setTimeout(() => {
                const settings = getMockSystemSettings()
                const currentUser = getCurrentUser()
                const users = getMockUsers()
                const teacher = users.find(item => item.id === currentUser?.id)
                const chargeMode = settings.classReportChargeMode || 'PERSON_AND_DIFFICULTY'
                const submittedCount = 1
                const reportableProblemCount = 1
                const easyProblemCount = 1
                const mediumProblemCount = 0
                const hardProblemCount = 0
                const perPersonMuCoinCost = Number(settings.classReportChargeEasy ?? 0)
                const classMuCoinCost = Number(settings.classReportChargeClass ?? 0)
                const totalMuCoinCost = chargeMode === 'CLASS_FLAT'
                    ? classMuCoinCost
                    : perPersonMuCoinCost * submittedCount
                const currentMuCoinCount = Number(teacher?.teacherMuCoinCount ?? 0)
                resolve({
                    code: 200,
                    message: '获取成功',
                    data: {
                        taskId: Number(taskId),
                        chargeMode,
                        submittedCount,
                        reportableProblemCount,
                        easyProblemCount,
                        mediumProblemCount,
                        hardProblemCount,
                        perPersonMuCoinCost,
                        classMuCoinCost,
                        totalMuCoinCost,
                        chargeRequired: true,
                        currentMuCoinCount,
                        enough: currentMuCoinCount >= totalMuCoinCost
                    }
                })
            }, 200)
        })
    },
    getMyMuCoinRecords: (page = 0, size = 20, filters = {}) => {
        return new Promise((resolve) => {
            setTimeout(() => {
                const currentUser = getCurrentUser()
                const records = getMockMuCoinRecords()
                    .filter(record => Number(record.teacherId) === Number(currentUser?.id))
                const filtered = filterMockMuCoinRecords(records, filters)
                resolve({ code: 200, message: '获取成功', data: pageMockMuCoinRecords(filtered, page, size) })
            }, 200)
        })
    },
    getAdminMuCoinRecords: (page = 0, size = 20, filters = {}) => {
        return new Promise((resolve) => {
            setTimeout(() => {
                const records = filterMockMuCoinRecords(getMockMuCoinRecords(), filters)
                resolve({ code: 200, message: '获取成功', data: pageMockMuCoinRecords(records, page, size) })
            }, 200)
        })
    },
    getAdminUsers: () => {
        return new Promise((resolve) => {
            setTimeout(() => {
                const users = getMockUsers()
                const classes = getMockClasses()
                const studentClasses = getMockStudentClasses()
                const filtered = users.filter(u => u.role === USER_ROLES.TEACHER || u.role === USER_ROLES.STUDENT)

                const items = filtered.map(u => {
                    const status = getMockUserStatus(u)
                    const base = {
                        id: u.id,
                        name: u.realname || u.email,
                        username: u.realname || u.email,
                        identityNo: u.identityNo || String(u.id),
                        email: u.email,
                        avatarUrl: u.avatarUrl || null,
                        phone: u.phone || '',
                        wechat: u.wechat || '',
                        github: u.github || '',
                        gitcode: u.gitcode || '',
                        status,
                        registerDate: '2024-01-01'
                    }
                    if (u.role === USER_ROLES.TEACHER) {
                        const teacherClasses = classes.filter(c => c.teacherId === u.id && !c.deleted)
                        const totalStudents = teacherClasses.reduce((sum, c) => sum + (c.studentCount || 0), 0)
                        return {
                            ...base,
                            role: 'teacher',
                            classCount: teacherClasses.length,
                            taskCount: 0,
                            extraStat: String(totalStudents),
                            teacherMaxCapacity: u.teacherMaxCapacity || 50,
                            teacherMaxSubmissionFileCount: u.teacherMaxSubmissionFileCount || 10,
                            teacherMaxSubmissionFileSize: u.teacherMaxSubmissionFileSize || 104857600,
                            teacherAllowCreateProblem: Boolean(u.teacherAllowCreateProblem),
                            teacherMuCoinCount: Number(u.teacherMuCoinCount || 0)
                        }
                    }
                    const classCount = studentClasses.filter(sc => sc.studentId === u.id).length
                    return {
                        ...base,
                        role: 'student',
                        classCount,
                        taskCount: 0,
                        extraStat: '--',
                        teacherMaxCapacity: null,
                        teacherMaxSubmissionFileCount: null,
                        teacherMaxSubmissionFileSize: null,
                        teacherAllowCreateProblem: null,
                        teacherMuCoinCount: null
                    }
                })

                const teacherCount = filtered.filter(u => u.role === USER_ROLES.TEACHER).length
                const studentCount = filtered.filter(u => u.role === USER_ROLES.STUDENT).length
                const stats = {
                    totalUsers: teacherCount + studentCount,
                    teacherCount,
                    studentCount,
                    activeUsers: items.filter(item => item.status === 'active').length
                }

                resolve({ code: 200, message: '获取成功', data: { stats, users: items } })
            }, 300)
        })
    },
    updateAdminUserStatus: (userId, active) => {
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                const users = getMockUsers()
                const target = users.find(u => u.id === userId)
                if (!target) {
                    reject({ response: { status: 404, data: { message: '用户不存在' } } })
                    return
                }
                target.deleted = !active
                target.disabled = !active
                saveMockUsers(users)
                resolve({ code: 200, message: '更新成功', data: { id: userId, status: getMockUserStatus(target) } })
            }, 300)
        })
    },
    updateAdminTeacherApproval: (teacherId, approved) => {
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                const users = getMockUsers()
                const target = users.find(u => u.id === teacherId)
                if (!target) {
                    reject({ response: { status: 404, data: { message: '用户不存在' } } })
                    return
                }
                if (target.role !== USER_ROLES.TEACHER) {
                    reject({ response: { status: 400, data: { message: '只能审核教师账号' } } })
                    return
                }
                if (target.deleted || target.disabled) {
                    reject({ response: { status: 400, data: { message: '账号已禁用，无法审核' } } })
                    return
                }
                target.teacherApproved = Boolean(approved)
                saveMockUsers(users)
                resolve({ code: 200, message: '更新成功', data: { id: teacherId, status: getMockUserStatus(target) } })
            }, 300)
        })
    },
    updateAdminUserInfo: (userId, data) => {
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                const users = getMockUsers()
                const target = users.find(u => u.id === userId)
                if (!target) {
                    reject({ response: { status: 404, data: { message: '用户不存在' } } })
                    return
                }
                const nextName = (data?.username || data?.name || '').trim()
                if (!nextName) {
                    reject({ response: { status: 400, data: { message: '姓名或用户名不能为空' } } })
                    return
                }
                if (data?.email) {
                    target.email = data.email.trim()
                }
                target.realname = nextName
                if (data?.identityNo !== undefined) {
                    const nextIdentityNo = String(data.identityNo || '').trim()
                    target.identityNo = nextIdentityNo || null
                }
                if (target.role === USER_ROLES.TEACHER && data?.teacherMuCoinCount !== undefined) {
                    const nextMuCoinCount = Number(data.teacherMuCoinCount)
                    if (!Number.isInteger(nextMuCoinCount) || nextMuCoinCount < 0) {
                        reject({ response: { status: 400, data: { message: '慕币数量必须为大于等于0的整数' } } })
                        return
                    }
                    target.teacherMuCoinCount = nextMuCoinCount
                }
                saveMockUsers(users)
                const role = target.role === USER_ROLES.TEACHER
                    ? 'teacher'
                    : (target.role === USER_ROLES.STUDENT ? 'student' : String(target.role || '').toLowerCase())
                resolve({
                    code: 200,
                    message: '更新成功',
                    data: {
                        id: target.id,
                        name: target.realname || target.email,
                        username: target.realname || target.email,
                        identityNo: target.identityNo || String(target.id),
                        email: target.email,
                        role,
                        status: getMockUserStatus(target),
                        teacherMuCoinCount: target.role === USER_ROLES.TEACHER ? Number(target.teacherMuCoinCount || 0) : null
                    }
                })
            }, 300)
        })
    },
    getAdminClasses: (page = 0, size = 50, filters = '') => {
        return new Promise((resolve) => {
            setTimeout(() => {
                const normalizedFilters = typeof filters === 'string' ? { search: filters } : (filters || {})
                const keyword = String(normalizedFilters.search || '').trim().toLowerCase()
                const teacherIdFilter = normalizedFilters.teacherId ? Number(normalizedFilters.teacherId) : null
                const yearFilter = normalizedFilters.year ? Number(normalizedFilters.year) : null
                const statusFilter = String(normalizedFilters.status || '').trim().toUpperCase()
                const allClasses = getMockClasses().filter(c => !c.deleted)
                const classes = allClasses.filter(c => {
                    if (c.deleted) return false
                    if (!keyword) return true
                    return [c.name, c.teacherName, c.code].some(value => String(value || '').toLowerCase().includes(keyword))
                }).filter(c => {
                    if (teacherIdFilter && Number(c.teacherId) !== teacherIdFilter) return false
                    if (yearFilter) {
                        const createdYear = c.createdAt ? new Date(c.createdAt).getFullYear() : null
                        if (createdYear !== yearFilter) return false
                    }
                    if (statusFilter === 'ACTIVE') return !c.disabled && c.allowJoin !== false
                    if (statusFilter === 'NO_JOIN') return !c.disabled && c.allowJoin === false
                    if (statusFilter === 'DISABLED') return Boolean(c.disabled)
                    return true
                })
                const users = getMockUsers()
                const teacherCount = users.filter(u => u.role === USER_ROLES.TEACHER).length
                const studentCount = users.filter(u => u.role === USER_ROLES.STUDENT).length
                const safePage = Math.max(0, Number(page) || 0)
                const safeSize = Math.max(1, Number(size) || 50)
                const total = classes.length
                const totalPages = total > 0 ? Math.ceil(total / safeSize) : 0
                const pagedClasses = classes.slice(safePage * safeSize, (safePage + 1) * safeSize)

                const items = pagedClasses.map(c => ({
                    id: c.id,
                    name: c.name,
                    code: c.code,
                    teacherId: c.teacherId,
                    teacherName: c.teacherName || '未知教师',
                    capacity: c.capacity || 0,
                    maxCapacity: c.maxCapacity || c.capacity || 0,
                    memberCount: c.studentCount || 0,
                    taskCount: c.taskCount || 0,
                    disabled: c.disabled || false,
                    allowJoin: c.allowJoin !== false,
                    createdAt: c.createdAt || null,
                    updatedAt: c.updatedAt || null,
                    joinDeadline: c.joinDeadline || null,
                    description: c.description || ''
                }))

                const stats = {
                    totalClasses: allClasses.length,
                    activeClasses: allClasses.filter(c => !c.disabled).length,
                    totalStudents: studentCount,
                    totalTeachers: teacherCount
                }
                const teacherMap = new Map()
                allClasses.forEach(c => {
                    if (c.teacherId) teacherMap.set(String(c.teacherId), c.teacherName || '未知教师')
                })
                const years = Array.from(new Set(allClasses
                    .map(c => c.createdAt ? new Date(c.createdAt).getFullYear() : null)
                    .filter(Boolean)))
                    .sort((a, b) => b - a)

                resolve({
                    code: 200,
                    message: '获取成功',
                    data: {
                        stats,
                        filterOptions: {
                            teachers: Array.from(teacherMap.entries()).map(([teacherId, teacherName]) => ({
                                teacherId: Number(teacherId),
                                teacherName
                            })),
                            years
                        },
                        classes: items,
                        page: safePage,
                        size: safeSize,
                        total,
                        totalPages
                    }
                })
            }, 300)
        })
    },
    updateAdminClass: (classId, data) => {
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                const classes = getMockClasses()
                const target = classes.find(c => c.id === classId && !c.deleted)
                if (!target) {
                    reject({ response: { status: 404, data: { message: '班级不存在' } } })
                    return
                }

                const name = data?.name !== undefined ? String(data.name || '').trim() : String(target.name || '').trim()
                const code = data?.code !== undefined ? String(data.code || '').trim() : String(target.code || '').trim()
                if (!name) {
                    reject({ response: { status: 400, data: { message: '班级名称不能为空' } } })
                    return
                }
                if (!code) {
                    reject({ response: { status: 400, data: { message: '班级邀请码不能为空' } } })
                    return
                }
                const duplicateCode = classes.find(c => !c.deleted && c.id !== target.id && String(c.code || '').trim() === code)
                if (duplicateCode) {
                    reject({ response: { status: 400, data: { message: '班级邀请码已存在' } } })
                    return
                }

                const memberCount = Number(target.studentCount) || 0
                const nextCapacity = data?.capacity !== undefined ? Number(data.capacity) : Number(target.capacity)
                const nextMaxCapacity = data?.maxCapacity !== undefined
                    ? Number(data.maxCapacity)
                    : Number(target.maxCapacity || target.capacity)

                if (!Number.isInteger(nextCapacity) || nextCapacity <= 0) {
                    reject({ response: { status: 400, data: { message: '班级容量必须为正整数' } } })
                    return
                }
                if (!Number.isInteger(nextMaxCapacity) || nextMaxCapacity <= 0) {
                    reject({ response: { status: 400, data: { message: '容量上限必须为正整数' } } })
                    return
                }
                if (nextCapacity < memberCount) {
                    reject({ response: { status: 400, data: { message: '容量不能小于当前班级人数' } } })
                    return
                }
                if (nextMaxCapacity < memberCount) {
                    reject({ response: { status: 400, data: { message: '容量上限不能小于当前班级人数' } } })
                    return
                }
                if (nextCapacity > nextMaxCapacity) {
                    reject({ response: { status: 400, data: { message: '班级容量不能大于容量上限' } } })
                    return
                }

                target.name = name
                target.code = code
                target.capacity = nextCapacity
                target.maxCapacity = nextMaxCapacity
                if (data?.joinDeadline !== undefined) target.joinDeadline = data.joinDeadline || null
                if (data?.description !== undefined) target.description = String(data.description || '').trim()
                if (data?.disabled !== undefined) target.disabled = Boolean(data.disabled)
                if (data?.allowJoin !== undefined) target.allowJoin = Boolean(data.allowJoin)
                target.updatedAt = new Date().toISOString()

                saveMockClasses(classes)
                resolve({ code: 200, message: '更新成功', data: target })
            }, 300)
        })
    },
    deleteAdminClass: (classId) => {
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                const classes = getMockClasses()
                const target = classes.find(c => c.id === classId && !c.deleted)
                if (!target) {
                    reject({ response: { status: 404, data: { message: '班级不存在' } } })
                    return
                }
                target.deleted = true
                saveMockClasses(classes)
                resolve({ code: 200, message: '删除成功' })
            }, 300)
        })
    },
    getAdminProblems: (params = {}) => {
        return new Promise((resolve) => {
            setTimeout(() => {
                const problems = getMockProblems()
                const safePage = Math.max(0, Number(params?.page) || 0)
                const safeSize = Math.max(1, Number(params?.size) || 50)
                const keyword = typeof params?.keyword === 'string' ? params.keyword.trim().toLowerCase() : ''
                const typeFilter = String(params?.type || '').trim().toUpperCase()
                const difficultyFilter = String(params?.difficulty || '').trim().toUpperCase()
                const statusFilter = String(params?.status || '').trim().toUpperCase()
                const rawPublicFilter = params?.isPublic
                const publicFilter = rawPublicFilter === true
                    ? true
                    : (rawPublicFilter === false ? false : undefined)
                const classes = getMockClasses().filter(c => !c.deleted)
                const users = getMockUsers()
                const classMap = new Map(classes.map(c => [c.id, c]))
                const userMap = new Map(users.map(u => [u.id, u]))
                const allItems = problems.map(p => {
                    const classroom = p.classroomId ? classMap.get(p.classroomId) : null
                    const creator = userMap.get(p.creatorId)
                    return {
                        id: p.id,
                        name: p.name,
                        type: p.type,
                        difficulty: p.difficulty,
                        status: p.status,
                        isPublic: p.isPublic,
                        creatorId: p.creatorId,
                        creatorName: creator?.realname || creator?.email || '',
                        creatorRole: creator?.role || '',
                        creatorOrg: creator?.companyName || '',
                        classroomId: p.classroomId,
                        classroomName: classroom?.name || '',
                        usageCount: p.usageCount || 0,
                        fileCount: p.fileCount || 0,
                        totalFileSize: p.totalFileSize || 0,
                        allowedExtensions: p.allowedExtensions || '',
                        createdAt: p.createdAt,
                        updatedAt: p.updatedAt
                    }
                })
                const filteredItems = allItems.filter(item => {
                    if (keyword) {
                        const matched = [
                            item.name,
                            item.creatorName,
                            item.creatorOrg,
                            item.classroomName
                        ].some(value => String(value || '').toLowerCase().includes(keyword))
                        if (!matched) return false
                    }
                    if (typeFilter && String(item.type || '').toUpperCase() !== typeFilter) return false
                    if (difficultyFilter && String(item.difficulty || '').toUpperCase() !== difficultyFilter) return false
                    if (statusFilter && String(item.status || '').toUpperCase() !== statusFilter) return false
                    if (publicFilter !== undefined && Boolean(item.isPublic) !== publicFilter) return false
                    return true
                })

                const total = filteredItems.length
                const totalPages = total > 0 ? Math.ceil(total / safeSize) : 0
                const items = filteredItems.slice(safePage * safeSize, (safePage + 1) * safeSize)

                const creatorSet = new Set(problems.map(i => i.creatorId).filter(Boolean))
                const stats = {
                    totalProblems: problems.length,
                    enabledProblems: problems.filter(p => p.status === 'ENABLED').length,
                    disabledProblems: problems.filter(p => p.status === 'DISABLED').length,
                    creatorCount: creatorSet.size
                }

                resolve({
                    code: 200,
                    message: '获取成功',
                    data: {
                        stats,
                        problems: items,
                        page: safePage,
                        size: safeSize,
                        total,
                        totalPages
                    }
                })
            }, 300)
        })
    },
    deleteAdminProblem: (problemId) => {
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                const problems = getMockProblems()
                const index = problems.findIndex(p => p.id === problemId)
                if (index < 0) {
                    reject({ response: { status: 404, data: { message: '题目不存在' } } })
                    return
                }
                problems.splice(index, 1)
                saveMockProblems(problems)
                resolve({ code: 200, message: '删除成功', data: null })
            }, 300)
        })
    },
    getAdminTasks: ({ page = 0, size = 50, keyword = '', timeStatus = '' } = {}) => {
        return new Promise((resolve) => {
            setTimeout(() => {
                const tasks = getMockTasks()
                const classes = getMockClasses().filter(c => !c.deleted)
                const users = getMockUsers()
                const classMap = new Map(classes.map(c => [c.id, c]))
                const userMap = new Map(users.map(u => [u.id, u]))
                const safePage = Number.isFinite(Number(page)) ? Math.max(Number(page), 0) : 0
                const safeSize = Number.isFinite(Number(size)) ? Math.min(Math.max(Number(size), 1), 200) : 50
                const normalizedKeyword = String(keyword || '').trim().toLowerCase()
                const normalizedTimeStatus = String(timeStatus || '').trim().toLowerCase()
                const now = new Date()
                const allItems = tasks.map(t => {
                    const classroom = classMap.get(t.classroomId)
                    const teacher = classroom ? userMap.get(classroom.teacherId) : userMap.get(t.creatorId)
                    return {
                        id: t.id,
                        name: t.name,
                        description: t.description,
                        classroomId: t.classroomId,
                        classroomName: classroom?.name || '',
                        teacherId: classroom?.teacherId || t.creatorId || null,
                        teacherName: classroom?.teacherName || teacher?.realname || teacher?.email || '',
                        taskType: t.taskType,
                        startTime: t.startTime,
                        endTime: t.endTime,
                        problemCount: t.problemCount || 0,
                        studentCount: classroom?.studentCount || 0,
                        submissionRate: t.submissionRate ?? 0,
                        averageScore: t.averageScore ?? null,
                        createdAt: t.createdAt,
                        updatedAt: t.updatedAt
                    }
                })
                const filteredItems = allItems.filter(item => {
                    if (normalizedKeyword && !String(item.name || '').toLowerCase().includes(normalizedKeyword)) {
                        return false
                    }
                    const start = item.startTime ? new Date(item.startTime) : null
                    const end = item.endTime ? new Date(item.endTime) : null
                    if (normalizedTimeStatus === 'ongoing') {
                        return Boolean(start && end && now >= start && now <= end)
                    }
                    if (normalizedTimeStatus === 'completed') {
                        return Boolean(end && now > end)
                    }
                    if (normalizedTimeStatus === 'pending') {
                        return Boolean(start && now < start)
                    }
                    return true
                })

                const total = filteredItems.length
                const totalPages = safeSize > 0 ? Math.ceil(total / safeSize) : 0
                const start = safePage * safeSize
                const pageItems = filteredItems.slice(start, start + safeSize)

                const teacherCount = users.filter(u => u.role === USER_ROLES.TEACHER).length
                const studentCount = users.filter(u => u.role === USER_ROLES.STUDENT).length
                const ongoingTasks = allItems.filter(item => {
                    const start = item.startTime ? new Date(item.startTime) : null
                    const end = item.endTime ? new Date(item.endTime) : null
                    return Boolean(start && end && now >= start && now <= end)
                }).length
                const completedTasks = allItems.filter(item => {
                    const end = item.endTime ? new Date(item.endTime) : null
                    return Boolean(end && now > end)
                }).length
                const stats = {
                    totalTeachers: teacherCount,
                    totalTasks: tasks.length,
                    totalStudents: studentCount,
                    totalClasses: classes.length,
                    ongoingTasks,
                    completedTasks
                }

                resolve({
                    code: 200,
                    message: '获取成功',
                    data: {
                        stats,
                        tasks: pageItems,
                        page: safePage,
                        size: safeSize,
                        total,
                        totalPages
                    }
                })
            }, 300)
        })
    },
    getAdminContests: () => {
        return new Promise((resolve) => {
            setTimeout(() => {
                const contests = getMockContests()
                const participations = getMockContestParticipations()
                const now = new Date()
                const items = contests.map(c => ({
                    id: c.id,
                    title: c.title,
                    registrationStart: c.registrationStart,
                    registrationEnd: c.registrationEnd,
                    contestStart: c.contestStart,
                    contestEnd: c.contestEnd,
                    problemCount: Array.isArray(c.problemIds) ? c.problemIds.length : (c.problemCount ?? 0),
                    resultPublished: c.resultPublished ?? false,
                    type: c.type || '',
                    level: c.level || '',
                    participantCount: participations.filter(p => p.contestId === c.id).length,
                    createdAt: c.createdAt || c.contestStart || c.registrationStart || null,
                    updatedAt: c.updatedAt || c.contestStart || c.registrationStart || null
                }))

                const ongoingContests = items.filter(item => {
                    const start = item.contestStart ? new Date(item.contestStart) : null
                    const end = item.contestEnd ? new Date(item.contestEnd) : null
                    if (!start || !end) return false
                    return now >= start && now <= end
                }).length

                const stats = {
                    totalContests: contests.length,
                    ongoingContests,
                    totalParticipants: participations.length,
                    totalProblems: items.reduce((sum, item) => sum + (item.problemCount || 0), 0)
                }

                resolve({ code: 200, message: '获取成功', data: { stats, contests: items } })
            }, 300)
        })
    },
    createAdminContest: (payload) => {
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                if (!payload?.title) {
                    reject({ response: { status: 400, data: { message: '大赛名称不能为空' } } })
                    return
                }
                if (!payload?.type) {
                    reject({ response: { status: 400, data: { message: '大赛类型不能为空' } } })
                    return
                }
                if (!payload?.level) {
                    reject({ response: { status: 400, data: { message: '大赛级别不能为空' } } })
                    return
                }
                if (!payload?.contestStart || !payload?.contestEnd) {
                    reject({ response: { status: 400, data: { message: '比赛时间不能为空' } } })
                    return
                }
                if (!Array.isArray(payload?.problemIds) || payload.problemIds.length === 0) {
                    reject({ response: { status: 400, data: { message: '请选择至少一个题目' } } })
                    return
                }
                const contests = getMockContests()
                const nextId = contests.length ? Math.max(...contests.map(c => c.id || 0)) + 1 : 1
                const now = new Date().toISOString()
                const contest = {
                    id: nextId,
                    title: payload.title,
                    description: payload.description || '',
                    registrationStart: payload.registrationStart || null,
                    registrationEnd: payload.registrationEnd || null,
                    contestStart: payload.contestStart,
                    contestEnd: payload.contestEnd,
                    officialUrl: payload.officialUrl || '',
                    detailUrl: payload.detailUrl || '',
                    rankingUrl: payload.rankingUrl || '',
                    resultUrl: payload.resultUrl || '',
                    problemCount: Array.isArray(payload.problemIds)
                        ? payload.problemIds.length
                        : (Number(payload.problemCount) || 0),
                    problemIds: Array.isArray(payload.problemIds) ? payload.problemIds : [],
                    resultPublished: Boolean(payload.resultPublished),
                    type: payload.type,
                    level: payload.level,
                    createdAt: now,
                    updatedAt: now
                }
                contests.unshift(contest)
                saveMockContests(contests)
                resolve({ code: 200, message: '创建成功', data: contest })
            }, 400)
        })
    },
    getAdminContestDetail: (contestId) => {
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                const contests = getMockContests()
                const contest = contests.find(c => c.id === contestId)
                if (!contest) {
                    reject({ response: { status: 404, data: { message: '比赛不存在' } } })
                    return
                }
                const participations = getMockContestParticipations()
                const problems = getMockProblems()
                const selectedIds = Array.isArray(contest.problemIds) ? contest.problemIds : []
                const problemCount = selectedIds.length > 0 ? selectedIds.length : (Number(contest.problemCount) || 0)
                const participantCount = participations.filter(p => p.contestId === contest.id).length
                const totalSlots = participantCount * (problemCount || 0)
                const submissionCount = totalSlots > 0 ? Math.round(totalSlots * 0.62) : 0
                const items = selectedIds.length > 0
                    ? selectedIds.map(id => problems.find(p => p.id === id)).filter(Boolean).map(p => ({
                        id: p.id,
                        name: p.name,
                        type: p.type,
                        difficulty: p.difficulty
                    }))
                    : problems.slice(0, problemCount).map(p => ({
                        id: p.id,
                        name: p.name,
                        type: p.type,
                        difficulty: p.difficulty
                    }))
                resolve({
                    code: 200,
                    message: '获取成功',
                    data: {
                        ...contest,
                        participantCount,
                        submissionCount,
                        submissionRate: totalSlots > 0 ? Math.min(1, submissionCount / totalSlots) : 0,
                        problems: items
                    }
                })
            }, 300)
        })
    },
    updateAdminContest: (contestId, payload) => {
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                const contests = getMockContests()
                const idx = contests.findIndex(c => c.id === contestId)
                if (idx < 0) {
                    reject({ response: { status: 404, data: { message: '比赛不存在' } } })
                    return
                }
                if (!payload?.title) {
                    reject({ response: { status: 400, data: { message: '大赛名称不能为空' } } })
                    return
                }
                if (!payload?.type) {
                    reject({ response: { status: 400, data: { message: '大赛类型不能为空' } } })
                    return
                }
                if (!payload?.level) {
                    reject({ response: { status: 400, data: { message: '大赛级别不能为空' } } })
                    return
                }
                if (!payload?.contestStart || !payload?.contestEnd) {
                    reject({ response: { status: 400, data: { message: '比赛时间不能为空' } } })
                    return
                }
                const now = new Date().toISOString()
                const next = {
                    ...contests[idx],
                    title: payload.title,
                    description: payload.description || '',
                    registrationStart: payload.registrationStart || null,
                    registrationEnd: payload.registrationEnd || null,
                    contestStart: payload.contestStart,
                    contestEnd: payload.contestEnd,
                    officialUrl: payload.officialUrl || '',
                    detailUrl: payload.detailUrl || '',
                    rankingUrl: payload.rankingUrl || '',
                    resultUrl: payload.resultUrl || '',
                    problemCount: Array.isArray(payload.problemIds)
                        ? payload.problemIds.length
                        : (Number(payload.problemCount) || 0),
                    problemIds: Array.isArray(payload.problemIds) ? payload.problemIds : contests[idx].problemIds,
                    resultPublished: Boolean(payload.resultPublished),
                    type: payload.type,
                    level: payload.level,
                    updatedAt: now
                }
                contests.splice(idx, 1, next)
                saveMockContests(contests)
                resolve({ code: 200, message: '更新成功', data: next })
            }, 400)
        })
    },

    // 学生加入班级
    joinClass: (classId, data) => {
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                const classes = getMockClasses()
                const classCode = String(data?.classCode || '').trim()
                const teacherName = String(data?.teacherName || '').trim()

                if (!classCode) {
                    reject({ response: { status: 400, data: { message: '班级邀请码不能为空' } } })
                    return
                }
                if (!teacherName) {
                    reject({ response: { status: 400, data: { message: '教师姓名不能为空' } } })
                    return
                }

                const targetClass = classes.find(c => c.id === classId || c.code === classCode)

                if (!targetClass) {
                    reject({ response: { status: 404, data: { message: '班级不存在' } } })
                    return
                }
                const actualTeacherName = String(targetClass.teacherName || '').trim()
                if (!actualTeacherName || actualTeacherName.toLowerCase() !== teacherName.toLowerCase()) {
                    reject({ response: { status: 400, data: { message: '教师姓名与班级邀请码不匹配' } } })
                    return
                }
                if (!targetClass.allowJoin) {
                    reject({ response: { status: 403, data: { message: '该班级不允许主动加入' } } })
                    return
                }

                const currentUser = getCurrentUser()
                const studentClasses = getMockStudentClasses()

                // 检查是否已加入
                if (studentClasses.find(sc => sc.studentId === currentUser.id && sc.classId === targetClass.id)) {
                    reject({ response: { status: 400, data: { message: '您已加入该班级' } } })
                    return
                }

                // 加入班级
                studentClasses.push({
                    studentId: currentUser.id,
                    classId: targetClass.id,
                    joinedAt: formatLocalDateOnly()
                })
                saveMockStudentClasses(studentClasses)

                // 更新班级人数
                targetClass.studentCount++
                saveMockClasses(classes)

                console.log('✅ Mock加入班级成功：', targetClass.name)
                resolve({ code: 200, message: '加入成功', data: targetClass })
            }, 500)
        })
    },
    leaveClass: (classId) => {
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                const currentUser = getCurrentUser()
                if (!currentUser?.id) {
                    reject({ response: { status: 401, data: { message: '未登录' } } })
                    return
                }
                const studentClasses = getMockStudentClasses()
                const targetIndex = studentClasses.findIndex(
                    sc => sc.studentId === currentUser.id && sc.classId === classId
                )
                if (targetIndex < 0) {
                    reject({ response: { status: 404, data: { message: '您不在该班级中' } } })
                    return
                }

                studentClasses.splice(targetIndex, 1)
                saveMockStudentClasses(studentClasses)

                const classes = getMockClasses()
                const targetClass = classes.find(c => c.id === classId)
                if (targetClass && Number(targetClass.studentCount) > 0) {
                    targetClass.studentCount -= 1
                    saveMockClasses(classes)
                }
                resolve({ code: 200, message: '退出成功', data: null })
            }, 300)
        })
    },

    // 获取学生班级列表
    getStudentClasses: (studentId) => {
        return new Promise((resolve) => {
            setTimeout(() => {
                const classes = getMockClasses()
                const studentClasses = getMockStudentClasses()
                const joinedClassIds = studentClasses.filter(sc => sc.studentId === studentId).map(sc => sc.classId)
                const list = classes.filter(c => joinedClassIds.includes(c.id))
                resolve({
                    code: 200,
                    data: {
                        list,
                        total: list.length
                    }
                })
            }, 300)
        })
    },

    // 获取学生测试大赛列表
    getStudentContests: (studentId, page = 0, size = 20) => {
        return new Promise((resolve) => {
            setTimeout(() => {
                const contests = getMockContests()
                const participations = getMockContestParticipations()
                const currentUser = getCurrentUser()
                const safePage = Math.max(page, 0)
                const start = safePage * size
                const end = start + size
                const slice = contests.slice(start, end)

                const items = slice.map(c => {
                    const participation = participations.find(p => p.contestId === c.id && p.studentId === currentUser?.id)
                    const participantCount = participations.filter(p => p.contestId === c.id).length
                    return {
                        ...c,
                        participantCount,
                        joined: !!participation,
                        progressCompleted: participation?.progressCompleted ?? null,
                        progressTotal: participation?.progressTotal ?? c.problemCount ?? 0,
                        rank: participation?.rank ?? null,
                        joinedAt: participation?.joinedAt ?? null
                    }
                })

                resolve({
                    code: 200,
                    data: {
                        items,
                        page: safePage,
                        size,
                        total: contests.length,
                        totalPages: Math.ceil(contests.length / size)
                    }
                })
            }, 300)
        })
    },

    getTeacherContestOverview: () => {
        return new Promise((resolve) => {
            setTimeout(() => {
                const currentUser = getCurrentUser()
                const teacherId = currentUser?.id
                const classes = getMockClasses().filter(c => c.teacherId === teacherId)
                const classResponses = classes.map(c => ({ id: c.id, name: c.name }))

                const users = getMockUsers()
                const students = users.filter(u => u.role === USER_ROLES.STUDENT)
                const studentClasses = getMockStudentClasses()
                const classById = new Map(classes.map(c => [c.id, c]))
                const studentById = new Map(students.map(s => [s.id, s]))

                const memberMap = new Map()
                studentClasses.forEach(sc => {
                    const classroom = classById.get(sc.classId)
                    if (!classroom) return
                    const key = sc.studentId
                    const current = memberMap.get(key) || { classIds: [], classNames: [] }
                    if (!current.classIds.includes(classroom.id)) {
                        current.classIds.push(classroom.id)
                        current.classNames.push(classroom.name)
                    }
                    memberMap.set(key, current)
                })

                const contests = getMockContests()
                const contestById = new Map(contests.map(c => [c.id, c]))
                const participations = getMockContestParticipations()
                const teacherStudentCount = {}
                const studentAggregateMap = new Map()

                participations.forEach(p => {
                    const contest = contestById.get(p.contestId)
                    const classroomInfo = memberMap.get(p.studentId)
                    const student = studentById.get(p.studentId)
                    if (!contest || !classroomInfo || !student) return
                    teacherStudentCount[contest.id] = (teacherStudentCount[contest.id] || 0) + 1

                    const progressCompleted = p.progressCompleted ?? 0
                    const progressTotal = p.progressTotal ?? contest.problemCount ?? 0
                    const joinedAtTs = p.joinedAt ? new Date(p.joinedAt).getTime() : null

                    const current = studentAggregateMap.get(student.id) || {
                        studentId: student.id,
                        studentName: student.realname || student.email,
                        identityNo: student.identityNo || '',
                        classId: classroomInfo.classIds[0] ?? null,
                        className: classroomInfo.classNames[0] ?? null,
                        classIds: [...classroomInfo.classIds],
                        classNames: [...classroomInfo.classNames],
                        contestId: null,
                        contestTitle: null,
                        contestIds: [],
                        contestTitles: [],
                        joinedAt: null,
                        rank: null,
                        progressCompleted: 0,
                        progressTotal: 0
                    }

                    if (!current.contestIds.includes(contest.id)) {
                        current.contestIds.push(contest.id)
                        current.contestTitles.push(contest.title)
                    }

                    const currentJoinedAtTs = current.joinedAt ? new Date(current.joinedAt).getTime() : null
                    const shouldReplace =
                        current.contestId == null ||
                        (joinedAtTs != null && (currentJoinedAtTs == null || joinedAtTs > currentJoinedAtTs)) ||
                        (
                            joinedAtTs != null &&
                            currentJoinedAtTs != null &&
                            joinedAtTs === currentJoinedAtTs &&
                            contest.id > (current.contestId || 0)
                        )

                    if (shouldReplace) {
                        current.contestId = contest.id
                        current.contestTitle = contest.title
                        current.joinedAt = p.joinedAt || null
                        current.rank = p.rank ?? null
                        current.progressCompleted = progressCompleted
                        current.progressTotal = progressTotal
                    }

                    studentAggregateMap.set(student.id, current)
                })

                const studentResponses = Array.from(studentAggregateMap.values())
                    .sort((a, b) => {
                        const at = a.joinedAt ? new Date(a.joinedAt).getTime() : -1
                        const bt = b.joinedAt ? new Date(b.joinedAt).getTime() : -1
                        return bt - at
                    })

                const contestResponses = contests.map(c => ({
                    id: c.id,
                    title: c.title,
                    registrationStart: c.registrationStart,
                    registrationEnd: c.registrationEnd,
                    contestStart: c.contestStart,
                    contestEnd: c.contestEnd,
                    officialUrl: c.officialUrl,
                    detailUrl: c.detailUrl,
                    rankingUrl: c.rankingUrl,
                    resultUrl: c.resultUrl,
                    problemCount: c.problemCount,
                    resultPublished: c.resultPublished,
                    participantCount: participations.filter(p => p.contestId === c.id).length,
                    teacherStudentCount: teacherStudentCount[c.id] || 0
                }))

                resolve({
                    code: 200,
                    data: {
                        contests: contestResponses,
                        students: studentResponses,
                        classes: classResponses
                    }
                })
            }, 300)
        })
    },

    // 学生报名测试大赛
    joinContest: (contestId) => {
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                const contests = getMockContests()
                const contest = contests.find(c => c.id === contestId)
                if (!contest) {
                    reject({ response: { status: 404, data: { message: '比赛不存在' } } })
                    return
                }
                const currentUser = getCurrentUser()
                if (!currentUser) {
                    reject({ response: { status: 401, data: { message: '请先登录' } } })
                    return
                }
                const participations = getMockContestParticipations()
                const existing = participations.find(p => p.contestId === contestId && p.studentId === currentUser.id)
                if (!existing) {
                    participations.push({
                        id: participations.length + 1,
                        contestId,
                        studentId: currentUser.id,
                        joinedAt: new Date().toISOString(),
                        progressCompleted: 0,
                        progressTotal: contest.problemCount ?? 0,
                        rank: null
                    })
                    saveMockContestParticipations(participations)
                }
                resolve({
                    code: 200,
                    message: '报名成功',
                    data: {
                        ...contest,
                        participantCount: participations.filter(p => p.contestId === contestId).length,
                        joined: true,
                        progressCompleted: 0,
                        progressTotal: contest.problemCount ?? 0,
                        rank: null,
                        joinedAt: new Date().toISOString()
                    }
                })
            }, 400)
        })
    },

    // 学生导入模板管理（Mock）
    uploadStudentTemplate: (file) => {
        return new Promise((resolve, reject) => {
            if (!file) {
                reject({ response: { status: 400, data: { message: '上传文件不能为空' } } })
                return
            }
            const reader = new FileReader()
            reader.onload = () => {
                const template = {
                    filename: file.name,
                    contentType: file.type || 'application/octet-stream',
                    size: file.size,
                    updatedAt: new Date().toISOString(),
                    dataUrl: reader.result
                }
                saveMockStudentTemplate(template)
                resolve({ code: 200, message: '上传成功', data: template })
            }
            reader.onerror = () => {
                reject({ response: { status: 500, data: { message: '读取文件失败' } } })
            }
            reader.readAsDataURL(file)
        })
    },
    getStudentTemplateInfo: () => {
        return new Promise((resolve, reject) => {
            const template = getMockStudentTemplate()
            if (!template) {
                reject({ response: { status: 404, data: { message: '模板尚未上传' } } })
                return
            }
            resolve({ code: 200, data: template })
        })
    },
    downloadStudentTemplate: () => {
        return new Promise((resolve, reject) => {
            const template = getMockStudentTemplate()
            if (!template) {
                reject({ response: { status: 404, data: { message: '模板尚未上传' } } })
                return
            }
            resolve({
                code: 200,
                data: {
                    blob: dataUrlToBlob(template.dataUrl, template.contentType),
                    filename: template.filename || 'student-import-template.xlsx'
                }
            })
        })
    },

    // 教师使用证明模板管理（Mock）
    uploadTeacherCertificateTemplate: (file) => {
        return new Promise((resolve, reject) => {
            if (!file) {
                reject({ response: { status: 400, data: { message: '上传文件不能为空' } } })
                return
            }
            const reader = new FileReader()
            reader.onload = () => {
                const template = {
                    filename: file.name,
                    contentType: file.type || 'application/octet-stream',
                    size: file.size,
                    updatedAt: new Date().toISOString(),
                    dataUrl: reader.result
                }
                saveMockTeacherCertTemplate(template)
                resolve({ code: 200, message: '上传成功', data: template })
            }
            reader.onerror = () => {
                reject({ response: { status: 500, data: { message: '读取文件失败' } } })
            }
            reader.readAsDataURL(file)
        })
    },
    getTeacherCertificateTemplateInfo: () => {
        return new Promise((resolve, reject) => {
            const template = getMockTeacherCertTemplate()
            if (!template) {
                reject({ response: { status: 404, data: { message: '教师使用证明模板尚未上传' } } })
                return
            }
            resolve({ code: 200, data: template })
        })
    },
    downloadTeacherCertificateTemplate: () => {
        return new Promise((resolve, reject) => {
            const template = getMockTeacherCertTemplate()
            if (!template) {
                reject({ response: { status: 404, data: { message: '教师使用证明模板尚未上传' } } })
                return
            }
            resolve({
                code: 200,
                data: {
                    blob: dataUrlToBlob(template.dataUrl, template.contentType),
                    filename: template.filename || 'teacher-certificate-template'
                }
            })
        })
    }
}

Object.keys(mockApi).forEach(key => {
    const original = mockApi[key]
    if (typeof original !== 'function') return

    mockApi[key] = (...args) => {
        const normalizedArgs = args.map(arg => normalizeRequestTimeFields(arg))
        return Promise.resolve()
            .then(() => original(...normalizedArgs))
            .then(response => normalizeResponseTimeFields(response))
    }
})

// ============ 真实 API 配置 ============
const apiClient = axios.create({
    baseURL: process.env.VUE_APP_API_URL || '/api',
    withCredentials: true,
    timeout: 10000,
    headers: {
        'Content-Type': 'application/json'
    }
})

// 请求拦截器
apiClient.interceptors.request.use(
    config => {
        const token = localStorage.getItem('token')
        if (token) {
            config.headers.Authorization = `Bearer ${token}`
        }

        if (config.data && !(config.data instanceof FormData)) {
            config.data = normalizeRequestTimeFields(config.data)
        }

        if (config.params) {
            config.params = normalizeRequestTimeFields(config.params)
        }

        return config
    },
    error => {
        return Promise.reject(error)
    }
)

// 账号被禁用的自定义弹窗（避免使用浏览器默认 alert）
let _disabledModalShowing = false
function showDisabledAccountModal() {
    if (_disabledModalShowing) return
    _disabledModalShowing = true

    const overlay = document.createElement('div')
    overlay.style.cssText = `
        position: fixed; top: 0; left: 0; right: 0; bottom: 0;
        background: rgba(0,0,0,0.45); backdrop-filter: blur(4px);
        display: flex; align-items: center; justify-content: center;
        z-index: 99999; animation: _dam_fadeIn 0.3s ease;
    `

    const card = document.createElement('div')
    card.style.cssText = `
        background: #fff; border-radius: 16px; padding: 36px 40px 28px;
        min-width: 340px; max-width: 420px; box-shadow: 0 20px 60px rgba(0,0,0,0.25);
        text-align: center; animation: _dam_scaleIn 0.35s ease;
    `

    card.innerHTML = `
        <div style="
            width: 56px; height: 56px; border-radius: 50%; margin: 0 auto 18px;
            background: linear-gradient(135deg, #f59e0b, #d97706);
            display: flex; align-items: center; justify-content: center;
        ">
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                <line x1="12" y1="9" x2="12" y2="13"></line>
                <line x1="12" y1="17" x2="12.01" y2="17"></line>
            </svg>
        </div>
        <h3 style="font-size: 20px; font-weight: 700; color: #d97706; margin: 0 0 10px;">账号已被禁用</h3>
        <p style="font-size: 14px; color: #6b7280; margin: 0 0 24px; line-height: 1.6;">
            您的账号已被管理员禁用，如有疑问请联系管理员处理。
        </p>
        <button id="_dam_confirm_btn" style="
            width: 100%; padding: 12px 0; border: none; border-radius: 10px;
            background: linear-gradient(135deg, #667eea, #764ba2); color: #fff;
            font-size: 15px; font-weight: 600; cursor: pointer;
            transition: opacity 0.2s ease; box-shadow: 0 4px 15px rgba(102,126,234,0.35);
        ">确认并重新登录</button>
    `

    overlay.appendChild(card)
    document.body.appendChild(overlay)

    // 注入动画关键帧
    if (!document.getElementById('_dam_keyframes')) {
        const style = document.createElement('style')
        style.id = '_dam_keyframes'
        style.textContent = `
            @keyframes _dam_fadeIn { from { opacity: 0; } to { opacity: 1; } }
            @keyframes _dam_scaleIn { from { opacity: 0; transform: scale(0.9) translateY(-20px); }
                50% { transform: scale(1.02) translateY(0); }
                to { opacity: 1; transform: scale(1) translateY(0); } }
        `
        document.head.appendChild(style)
    }

    const confirmBtn = card.querySelector('#_dam_confirm_btn')
    confirmBtn.addEventListener('mouseenter', () => { confirmBtn.style.opacity = '0.85' })
    confirmBtn.addEventListener('mouseleave', () => { confirmBtn.style.opacity = '1' })
    confirmBtn.addEventListener('click', () => {
        _disabledModalShowing = false
        document.body.removeChild(overlay)
        window.location.href = '/login'
    })
}

// 响应拦截器
apiClient.interceptors.response.use(
    response => {
        return normalizeResponseTimeFields(response.data)
    },
    error => {
        if (error.response) {
            const requestUrl = error.config?.url || ''
            // 登录和注册接口的 401 错误不应触发自动跳转，让调用方处理
            const isAuthEndpoint = requestUrl.includes('/auth/login') || requestUrl.includes('/auth/register')

            switch (error.response.status) {
                case 401:
                    if (!isAuthEndpoint) {
                        localStorage.removeItem('token')
                        localStorage.removeItem('currentUser')
                        window.location.href = '/login'
                    }
                    break
                case 403: {
                    const message = error.response.data?.message || error.response.data?.reason || ''
                    if (message.includes('账号已被禁用')) {
                        localStorage.removeItem('token')
                        localStorage.removeItem('currentUser')
                        showDisabledAccountModal()
                    } else {
                        console.error('没有权限访问')
                    }
                    break
                }
                case 500:
                    console.error('服务器错误')
                    break
            }
        }
        sanitizeApiError(error)
        return Promise.reject(error)
    }
)

// ============ 导出 API 方法 ============

export const login = (data) => {
    if (USE_MOCK) {
        console.log('🔧 [Mock模式] 登录请求：', data)
        return mockApi.login(data)
    }
    return apiClient.post('/auth/login', data)
}

export const register = (data) => {
    if (USE_MOCK) {
        console.log('🔧 [Mock模式] 注册请求：', data)
        return mockApi.register(data)
    }
    return apiClient.post('/auth/register', data)
}

export const forgotPassword = (email) => {
    return apiClient.post('/auth/forgot-password', { email })
}

export const resetPassword = (token, newPassword, confirmPassword) => {
    return apiClient.post('/auth/reset-password', { token, newPassword, confirmPassword })
}

export const getUserProfile = (userId) => {
    if (USE_MOCK) {
        console.log('🔧 [Mock模式] 获取用户信息：', userId)
        return mockApi.getUserProfile(userId)
    }
    return apiClient.get('/users/me/profile')
}

export const getUserHomepage = () => {
    if (USE_MOCK) {
        const currentUser = getCurrentUser()
        if (!currentUser?.id) {
            return Promise.reject(new Error('未登录'))
        }
        return mockApi.getUserProfile(currentUser.id).then((res) => {
            const profile = res.data || res
            const role = profile.role
            let teacherStats = null
            let studentStats = null
            let adminStats = null

            if (role === USER_ROLES.TEACHER) {
                const classes = getMockClasses().filter(c => c.teacherId === currentUser.id && !c.deleted)
                const tasks = getMockTasks().filter(t => t.creatorId === currentUser.id)
                const problems = getMockProblems().filter(p => p.creatorId === currentUser.id)
                teacherStats = {
                    classCount: classes.length,
                    studentCount: classes.reduce((sum, c) => sum + Number(c.studentCount || 0), 0),
                    taskCount: tasks.length,
                    problemCount: problems.length,
                    maxCapacity: profile.teacherMaxCapacity || 50,
                    maxSubmissionFileCount: profile.teacherMaxSubmissionFileCount || 10,
                    maxSubmissionFileSize: profile.teacherMaxSubmissionFileSize || 104857600,
                    allowCreateProblem: Boolean(profile.teacherAllowCreateProblem)
                }
            } else if (role === USER_ROLES.STUDENT) {
                const joinedClasses = getMockStudentClasses().filter(item => item.studentId === currentUser.id)
                studentStats = {
                    joinedClassCount: joinedClasses.length,
                    joinedTaskCount: 0,
                    submittedTaskCount: 0,
                    averageScore: null,
                    joinedContestCount: 0
                }
            } else if (role === USER_ROLES.ADMIN) {
                const users = getMockUsers().filter(u => !u.deleted)
                adminStats = {
                    totalUserCount: users.length,
                    teacherCount: users.filter(u => u.role === USER_ROLES.TEACHER).length,
                    studentCount: users.filter(u => u.role === USER_ROLES.STUDENT).length,
                    activeClassCount: getMockClasses().filter(c => !c.deleted && !c.disabled).length,
                    totalTaskCount: getMockTasks().length,
                    totalContestCount: getMockContests().length,
                    totalProblemCount: getMockProblems().length
                }
            }

            return {
                code: 200,
                message: 'OK',
                data: {
                    profile,
                    teacherStats,
                    studentStats,
                    adminStats
                }
            }
        })
    }
    return apiClient.get('/users/me/homepage')
}

export const updateUserProfile = (data) => {
    return apiClient.post('/users/me/profile', data)
}

export const changeUserPassword = (data) => {
    return apiClient.post('/users/me/password', data)
}

export const uploadUserAvatar = (file) => {
    const formData = new FormData()
    formData.append('file', file)
    return apiClient.post('/users/me/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000
    })
}

export const getCurrentUserInfo = () => {
    if (USE_MOCK) {
        return mockApi.getCurrentUserInfo()
    }
    return apiClient.get('/user/info')
}

// ============ 班级管理 API ============

// 创建班级
export const createClass = (teacherId, data) => {
    if (USE_MOCK) {
        console.log('🔧 [Mock模式] 创建班级：', { teacherId, data })
        return mockApi.createClass(teacherId, data)
    }
    return apiClient.post('/classes', data)
}

// 获取教师班级列表
export const getTeacherClasses = (teacherId, page = 0, size = 20, search = '') => {
    if (USE_MOCK) {
        console.log('🔧 [Mock模式] 获取教师班级列表：', teacherId)
        return mockApi.getTeacherClasses(teacherId, page, size, search)
    }
    return apiClient.get('/classes', { params: { page, size, search: search || undefined } })
}

// 获取教师班级详情
export const getTeacherClassDetail = (classId) => {
    if (USE_MOCK) {
        const currentUser = getCurrentUser()
        return mockApi.getTeacherClassDetail(currentUser?.id, classId)
    }
    return apiClient.get(`/classes/${classId}/manage`)
}

// 更新班级
export const updateClass = (classId, data) => {
    if (USE_MOCK) {
        const currentUser = getCurrentUser()
        return mockApi.updateClass(currentUser?.id, classId, data)
    }
    return apiClient.post(`/classes/${classId}`, data)
}

// 删除班级（逻辑删除）
export const deleteClass = (classId) => {
    if (USE_MOCK) {
        const currentUser = getCurrentUser()
        return mockApi.deleteClass(currentUser?.id, classId)
    }
    return apiClient.delete(`/classes/${classId}`)
}

// 更新班级容量上限（管理员）
export const updateMaxCapacity = (classId, maxCapacity) => {
    if (USE_MOCK) {
        return mockApi.updateMaxCapacity(null, classId, maxCapacity)
    }
    return apiClient.post(`/classes/${classId}/max-capacity`, { maxCapacity })
}

// 更新教师班级最大容量（管理员）
export const updateTeacherMaxCapacity = (teacherId, maxCapacity) => {
    if (USE_MOCK) {
        return mockApi.updateTeacherMaxCapacity(teacherId, maxCapacity)
    }
    return apiClient.post(`/admin/teachers/${teacherId}/max-capacity`, { maxCapacity })
}

// 更新教师提交限制（管理员）
export const updateTeacherSubmissionLimits = (teacherId, maxFileCount, maxFileSize, allowCreateProblem) => {
    if (USE_MOCK) {
        return mockApi.updateTeacherSubmissionLimits(teacherId, maxFileCount, maxFileSize, allowCreateProblem)
    }
    return apiClient.post(`/admin/teachers/${teacherId}/submission-limits`, {
        maxFileCount,
        maxFileSize,
        allowCreateProblem
    })
}

// 获取系统上传限制（管理员）
export const getAdminUploadLimits = () => {
    if (USE_MOCK) {
        return mockApi.getAdminUploadLimits()
    }
    return apiClient.get('/admin/system/upload-limits')
}

// 更新系统上传限制（管理员）
export const updateAdminUploadLimits = (maxFileSize, maxRequestSize) => {
    if (USE_MOCK) {
        return mockApi.updateAdminUploadLimits(maxFileSize, maxRequestSize)
    }
    return apiClient.post('/admin/system/upload-limits', { maxFileSize, maxRequestSize })
}

export const getAdminProblemGradingChargeConfig = () => {
    if (USE_MOCK) {
        return mockApi.getAdminProblemGradingChargeConfig()
    }
    return apiClient.get('/admin/system/problem-grading-charge')
}

export const updateAdminProblemGradingChargeConfig = (easyMuCoinPerPerson, mediumMuCoinPerPerson, hardMuCoinPerPerson) => {
    if (USE_MOCK) {
        return mockApi.updateAdminProblemGradingChargeConfig(
            easyMuCoinPerPerson,
            mediumMuCoinPerPerson,
            hardMuCoinPerPerson
        )
    }
    return apiClient.post('/admin/system/problem-grading-charge', {
        easyMuCoinPerPerson,
        mediumMuCoinPerPerson,
        hardMuCoinPerPerson
    })
}

export const getAdminTaskReportChargeConfig = () => {
    if (USE_MOCK) {
        return mockApi.getAdminTaskReportChargeConfig()
    }
    return apiClient.get('/admin/system/task-report-charge')
}

export const updateAdminTaskReportChargeConfig = (
    chargeMode,
    easyMuCoinPerPerson,
    mediumMuCoinPerPerson,
    hardMuCoinPerPerson,
    classMuCoinCost
) => {
    if (USE_MOCK) {
        return mockApi.updateAdminTaskReportChargeConfig(
            chargeMode,
            easyMuCoinPerPerson,
            mediumMuCoinPerPerson,
            hardMuCoinPerPerson,
            classMuCoinCost
        )
    }
    return apiClient.post('/admin/system/task-report-charge', {
        chargeMode,
        easyMuCoinPerPerson,
        mediumMuCoinPerPerson,
        hardMuCoinPerPerson,
        classMuCoinCost
    })
}

export const getAdminTaskClassReportChargeConfig = () => {
    if (USE_MOCK) {
        return mockApi.getAdminTaskClassReportChargeConfig()
    }
    return apiClient.get('/admin/system/task-class-report-charge')
}

export const updateAdminTaskClassReportChargeConfig = (
    chargeMode,
    easyMuCoinPerPerson,
    mediumMuCoinPerPerson,
    hardMuCoinPerPerson,
    classMuCoinCost
) => {
    if (USE_MOCK) {
        return mockApi.updateAdminTaskClassReportChargeConfig(
            chargeMode,
            easyMuCoinPerPerson,
            mediumMuCoinPerPerson,
            hardMuCoinPerPerson,
            classMuCoinCost
        )
    }
    return apiClient.post('/admin/system/task-class-report-charge', {
        chargeMode,
        easyMuCoinPerPerson,
        mediumMuCoinPerPerson,
        hardMuCoinPerPerson,
        classMuCoinCost
    })
}

// 获取管理员用户列表
export const getAdminUsers = (page = 0, size = 20, search = '', role = '', onlyPending = false) => {
    if (USE_MOCK) {
        return mockApi.getAdminUsers()
    }
    return apiClient.get('/admin/users', { params: { page, size, search: search || undefined, role: role || undefined, onlyPending } })
}

export const getMyMuCoinRecords = (page = 0, size = 20, filters = {}) => {
    if (USE_MOCK) {
        return mockApi.getMyMuCoinRecords(page, size, filters)
    }
    return apiClient.get('/users/me/mu-coin-records', {
        params: {
            page,
            size,
            recordType: filters.recordType || undefined,
            scene: filters.scene || undefined,
            startTime: filters.startTime || undefined,
            endTime: filters.endTime || undefined
        }
    })
}

export const getAdminMuCoinRecords = (page = 0, size = 20, filters = {}) => {
    if (USE_MOCK) {
        return mockApi.getAdminMuCoinRecords(page, size, filters)
    }
    return apiClient.get('/admin/mu-coin-records', {
        params: {
            page,
            size,
            teacherId: filters.teacherId || undefined,
            teacherKeyword: filters.teacherKeyword || undefined,
            recordType: filters.recordType || undefined,
            scene: filters.scene || undefined,
            startTime: filters.startTime || undefined,
            endTime: filters.endTime || undefined
        }
    })
}

const normalizeAdminClassFilters = (filters = '') => {
    if (typeof filters === 'string') {
        return { search: filters }
    }
    return filters || {}
}

// 获取管理员班级列表
export const getAdminClasses = (page = 0, size = 50, filters = '') => {
    const normalizedFilters = normalizeAdminClassFilters(filters)
    const status = String(normalizedFilters.status || '').trim().toUpperCase()
    if (USE_MOCK) {
        return mockApi.getAdminClasses(page, size, normalizedFilters)
    }
    return apiClient.get('/admin/classes', {
        params: {
            page,
            size,
            search: normalizedFilters.search || undefined,
            teacherId: normalizedFilters.teacherId || undefined,
            year: normalizedFilters.year || undefined,
            status: status || undefined
        }
    })
}

// 更新班级信息（管理员）
export const updateAdminClass = (classId, data) => {
    if (USE_MOCK) {
        return mockApi.updateAdminClass(classId, data)
    }
    return apiClient.post(`/admin/classes/${classId}`, data)
}

// 删除班级（管理员）
export const deleteAdminClass = (classId) => {
    if (USE_MOCK) {
        return mockApi.deleteAdminClass(classId)
    }
    return apiClient.delete(`/admin/classes/${classId}`)
}

// 获取管理员题目列表
export const getAdminProblems = (page = 0, size = 50, filters = {}) => {
    const keyword = typeof filters?.keyword === 'string' ? filters.keyword.trim() : ''
    const normalizedStatus = String(filters?.status || '').trim().toUpperCase()
    const isPublicParam = filters?.isPublic === true ? true : (filters?.isPublic === false ? false : undefined)
    if (USE_MOCK) {
        return mockApi.getAdminProblems({
            page,
            size,
            keyword: keyword || undefined,
            type: filters?.type || undefined,
            difficulty: filters?.difficulty || undefined,
            status: normalizedStatus || undefined,
            isPublic: isPublicParam
        })
    }
    return apiClient.get('/admin/problems', {
        params: {
            page,
            size,
            keyword: keyword || undefined,
            type: filters?.type || undefined,
            difficulty: filters?.difficulty || undefined,
            status: normalizedStatus || undefined,
            isPublic: isPublicParam
        }
    })
}

// 删除管理员题目
export const deleteAdminProblem = (problemId) => {
    if (USE_MOCK) {
        return mockApi.deleteAdminProblem(problemId)
    }
    return apiClient.delete(`/admin/problems/${problemId}`)
}

// 获取管理员任务列表
export const getAdminTasks = (page = 0, size = 50, filters = {}) => {
    if (USE_MOCK) {
        return mockApi.getAdminTasks({ page, size, keyword: filters?.keyword, timeStatus: filters?.timeStatus })
    }
    const params = { page, size }
    if (filters?.keyword) {
        params.keyword = filters.keyword
    }
    if (filters?.timeStatus) {
        params.timeStatus = filters.timeStatus
    }
    return apiClient.get('/admin/tasks', { params })
}

// 获取管理员大赛列表
export const getAdminContests = (page = 0, size = 50) => {
    if (USE_MOCK) {
        return mockApi.getAdminContests()
    }
    return apiClient.get('/admin/contests', { params: { page, size } })
}

export const createAdminContest = (data) => {
    if (USE_MOCK) {
        return mockApi.createAdminContest(data)
    }
    return apiClient.post('/admin/contests', data)
}

export const getAdminContestDetail = (contestId) => {
    if (USE_MOCK) {
        return mockApi.getAdminContestDetail(contestId)
    }
    return apiClient.get(`/admin/contests/${contestId}`)
}

export const updateAdminContest = (contestId, data) => {
    if (USE_MOCK) {
        return mockApi.updateAdminContest(contestId, data)
    }
    return apiClient.post(`/admin/contests/${contestId}`, data)
}

export const getAdminContestAnnouncements = (contestId) => {
    return apiClient.get(`/admin/contests/${contestId}/announcements`)
}

export const createAdminContestAnnouncement = (contestId, data) => {
    return apiClient.post(`/admin/contests/${contestId}/announcements`, data)
}

export const getAdminContestGlobalAnnouncements = () => {
    return apiClient.get('/admin/contests/announcements')
}

export const createAdminContestGlobalAnnouncement = (data) => {
    return apiClient.post('/admin/contests/announcements', data)
}

export const updateAdminContestGlobalAnnouncement = (announcementId, data) => {
    return apiClient.post(`/admin/contests/announcements/${announcementId}`, data)
}

export const deleteAdminContestGlobalAnnouncement = (announcementId) => {
    return apiClient.delete(`/admin/contests/announcements/${announcementId}`)
}

export const reorderAdminContestGlobalAnnouncements = (announcementIds) => {
    return apiClient.post('/admin/contests/announcements/reorder', { announcementIds })
}

// 更新用户状态（启用/禁用）
export const updateAdminUserStatus = (userId, active) => {
    if (USE_MOCK) {
        return mockApi.updateAdminUserStatus(userId, active)
    }
    return apiClient.post(`/admin/users/${userId}/status`, { active })
}

// 更新教师审核状态（管理员）
export const updateAdminTeacherApproval = (teacherId, approved) => {
    if (USE_MOCK) {
        return mockApi.updateAdminTeacherApproval(teacherId, approved)
    }
    return apiClient.post(`/admin/teachers/${teacherId}/approval`, { approved })
}

// 更新用户信息（管理员）
export const updateAdminUserInfo = (userId, data) => {
    if (USE_MOCK) {
        return mockApi.updateAdminUserInfo(userId, data)
    }
    return apiClient.post(`/admin/users/${userId}`, data)
}

// 删除用户（管理员）
export const deleteAdminUser = (userId) => {
    return apiClient.delete(`/admin/users/${userId}`)
}

// 获取班级统计
export const getClassSummary = () => {
    return apiClient.get('/classes/summary')
}

// 获取班级成员
export const getClassMembers = (classId, page = 0, size = 20) => {
    return apiClient.get(`/classes/${classId}/members`, { params: { page, size } })
}

// 移除班级成员（管理员/教师）
export const removeClassMember = (classId, studentId) => {
    return apiClient.delete(`/classes/${classId}/members/${studentId}`)
}

// 邀请学生加入班级
export const inviteStudent = (classId, studentId) => {
    return apiClient.post(`/classes/${classId}/invite`, { studentId })
}

// 更新班级容量
export const updateClassCapacity = (classId, capacity) => {
    return apiClient.post(`/classes/${classId}/capacity`, { capacity })
}

// 学生加入班级
export const joinClass = (classId, data) => {
    if (USE_MOCK) {
        console.log('🔧 [Mock模式] 加入班级：', { classId, data })
        return mockApi.joinClass(classId, data)
    }
    if (classId) {
        return apiClient.post(`/classes/${classId}/join`, data)
    } else {
        return apiClient.post(`/classes/join`, {
            code: data.classCode,
            teacherName: data.teacherName
        })
    }
}

// 学生退出班级
export const leaveClass = (classId) => {
    if (USE_MOCK) {
        return mockApi.leaveClass(classId)
    }
    return apiClient.delete(`/classes/${classId}/leave`)
}

// 获取学生班级列表
export const getStudentClasses = (studentId, page = 0, size = 200) => {
    if (USE_MOCK) {
        console.log('🔧 [Mock模式] 获取学生班级列表：', studentId)
        return mockApi.getStudentClasses(studentId)
    }
    // 使用新的 API 端点 - 基于当前登录学生的 session
    return apiClient.get('/classes/student/my', { params: { page, size } })
}

// 获取学生班级详情
export const getStudentClassDetail = (classId) => {
    if (USE_MOCK) {
        console.log('🔧 [Mock模式] 获取学生班级详情：', classId)
        // 简单模拟
        return Promise.resolve({ code: 200, data: { id: classId, name: '模拟班级' } })
    }
    return apiClient.get(`/classes/${classId}`)
}

// 批量导入学生
export const importStudents = (classId, file) => {
    if (USE_MOCK) {
        console.log('🔧 [Mock模式] 批量导入学生：', classId, file.name)
        return Promise.resolve({ code: 200, message: '导入成功（模拟）', data: { imported: 0 } })
    }
    const formData = new FormData()
    formData.append('file', file)
    return apiClient.post(`/classes/${classId}/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000
    })
}

// ============ 学生导入模板 API ============
export const uploadStudentTemplate = (file) => {
    if (USE_MOCK) {
        console.log('🔧 [Mock模式] 上传学生导入模板：', file?.name)
        return mockApi.uploadStudentTemplate(file)
    }
    const formData = new FormData()
    formData.append('file', file)
    return apiClient.post('/templates/student-import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000
    })
}

export const getStudentTemplateInfo = () => {
    if (USE_MOCK) {
        return mockApi.getStudentTemplateInfo()
    }
    return apiClient.get('/templates/student-import')
}

export const downloadStudentTemplate = () => {
    if (USE_MOCK) {
        return mockApi.downloadStudentTemplate()
    }
    return apiClient.get('/templates/student-import/download', { responseType: 'blob' })
}

// ============ 教师使用证明模板 API ============
export const uploadTeacherCertificateTemplate = (file) => {
    if (USE_MOCK) {
        console.log('🔧 [Mock模式] 上传教师使用证明模板：', file?.name)
        return mockApi.uploadTeacherCertificateTemplate(file)
    }
    const formData = new FormData()
    formData.append('file', file)
    return apiClient.post('/templates/teacher-certificate', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000
    })
}

export const getTeacherCertificateTemplateInfo = () => {
    if (USE_MOCK) {
        return mockApi.getTeacherCertificateTemplateInfo()
    }
    return apiClient.get('/templates/teacher-certificate')
}

export const downloadTeacherCertificateTemplate = () => {
    if (USE_MOCK) {
        return mockApi.downloadTeacherCertificateTemplate()
    }
    return apiClient.get('/templates/teacher-certificate/download', { responseType: 'blob' })
}

export const downloadTeacherCertificateTemplatePublic = () => {
    if (USE_MOCK) {
        return mockApi.downloadTeacherCertificateTemplate()
    }
    return apiClient.get('/templates/teacher-certificate/public-download', { responseType: 'blob' })
}

// ============ 测试大赛 API ============
export const getTeacherContestOverview = () => {
    if (USE_MOCK) {
        return mockApi.getTeacherContestOverview()
    }
    return apiClient.get('/contests/teacher/overview')
}

export const getContests = (page = 0, size = 20) => {
    return apiClient.get('/contests', { params: { page, size } })
}

export const joinContest = (contestId) => {
    return apiClient.post(`/contests/${contestId}/join`)
}

export const getContestDetail = (contestId) => {
    return apiClient.get(`/contests/${contestId}`)
}

export const getContestAnnouncements = (contestId) => {
    return apiClient.get(`/contests/${contestId}/announcements`)
}

export const getContestAnnouncementDetail = (contestId, announcementId) => {
    return apiClient.get(`/contests/${contestId}/announcements/${announcementId}`)
}

export const getContestGlobalAnnouncements = () => {
    return apiClient.get('/contests/announcements')
}

export const getContestGlobalAnnouncementDetail = (announcementId) => {
    return apiClient.get(`/contests/announcements/${announcementId}`)
}

export const submitContestProblem = (contestId, problemId, files) => {
    const payload = Array.isArray(files) ? files : [files]
    const formData = new FormData()
    payload.forEach(file => {
        if (file) formData.append('files', file)
    })
    return apiClient.post(`/contests/${contestId}/problems/${problemId}/submit`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000
    })
}

// ============ 任务管理 API ============

// 获取任务列表
export const getTasks = (page = 0, size = 20, filters = {}) => {
    const params = { page, size }
    if (filters?.classId) {
        params.classId = filters.classId
    }
    if (filters?.keyword) {
        params.keyword = filters.keyword
    }
    if (filters?.timeStatus) {
        params.timeStatus = filters.timeStatus
    }
    if (filters?.problemType) {
        params.problemType = filters.problemType
    }
    return apiClient.get('/tasks', { params })
}

// 获取任务详情
export const getTaskDetail = (taskId) => {
    return apiClient.get(`/tasks/${taskId}`)
}

// 创建任务
export const createTask = (data) => {
    return apiClient.post('/tasks', data)
}

// 更新任务（管理员/教师）
export const updateTask = (taskId, data) => {
    return apiClient.post(`/tasks/${taskId}`, data)
}

// 设置任务成绩是否对学生公开（教师/管理员）
export const updateTaskScorePublication = (taskId, scoresPublished) => {
    return apiClient.post(`/tasks/${taskId}/scores/publication`, { scoresPublished })
}

// 上传任务成绩和报告地址 Excel（教师/管理员）
export const uploadTaskScores = (taskId, file) => {
    const formData = new FormData()
    formData.append('file', file)
    return apiClient.post(`/tasks/${taskId}/scores/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000
    })
}

export const exportTaskScores = (taskId) => {
    return apiClient.get(`/tasks/${taskId}/scores/export`, {
        responseType: 'blob',
        timeout: 300000
    })
}

// 删除任务（管理员/教师）
export const deleteTask = (taskId) => {
    return apiClient.delete(`/tasks/${taskId}`)
}

// 学生加入任务
export const joinTask = (taskId) => {
    return apiClient.post(`/tasks/${taskId}/join`)
}

// 学生退出任务
export const leaveTask = (taskId) => {
    return apiClient.delete(`/tasks/${taskId}/leave`)
}

// 学生提交任务
export const submitTask = (taskId, score) => {
    return apiClient.post(`/tasks/${taskId}/submit`, { score })
}

// 获取任务进度
export const getTaskProgress = (taskId) => {
    return apiClient.get(`/tasks/${taskId}/progress`)
}

// 触发任务报告生成（教师/管理员）
export const generateTaskReport = (taskId) => {
    return apiClient.post(`/tasks/${taskId}/reports/generate`)
}

export const getTaskReportChargePreview = (taskId) => {
    if (USE_MOCK) {
        return mockApi.getTaskReportChargePreview(taskId)
    }
    return apiClient.get(`/tasks/${taskId}/reports/generate-charge-preview`)
}

export const getTaskClassReportChargePreview = (taskId) => {
    if (USE_MOCK) {
        return mockApi.getTaskClassReportChargePreview(taskId)
    }
    return apiClient.get(`/tasks/${taskId}/reports/class/generate-charge-preview`)
}

// 查询任务报告生成状态（教师/管理员）
export const getTaskReportStatus = (taskId) => {
    return apiClient.get(`/tasks/${taskId}/reports/status`)
}

// 触发班级学情分析生成（教师/管理员）
export const generateTaskClassAnalysis = (taskId) => {
    return apiClient.post(`/tasks/${taskId}/reports/class/generate`)
}

// 查询班级学情分析状态（教师/管理员）
export const getTaskClassAnalysisStatus = (taskId) => {
    return apiClient.get(`/tasks/${taskId}/reports/class/status`)
}

// 获取班级开发者测试评估报告摘要（教师/管理员）
export const getTaskClassReportSummary = (taskId) => {
    return apiClient.get(`/tasks/${taskId}/reports/class/summary`)
}

// 获取学生开发者测试报告下载链接（教师/管理员）
export const getTaskStudentReportDownloadUrl = (taskId, problemId, studentId) => {
    return apiClient.get(`/tasks/${taskId}/reports/students/${studentId}/download-url`, {
        params: { problemId }
    })
}

// 获取学生开发者测试报告查看链接（教师/管理员）
export const getTaskStudentReportViewUrl = (taskId, problemId, studentId) => {
    return apiClient.get(`/tasks/${taskId}/reports/students/${studentId}/view-url`, {
        params: { problemId }
    })
}

// 获取当前学生自己的任务报告下载链接
export const getMyTaskReportDownloadUrl = (taskId, problemId = null) => {
    return apiClient.get(`/tasks/${taskId}/reports/me/download-url`, {
        params: problemId ? { problemId } : {}
    })
}

// 提醒学生提交任务（教师）
export const remindTaskStudent = (taskId, studentId) => {
    return apiClient.post(`/tasks/${taskId}/remind`, { studentId })
}

// 学生提交任务题目附件
export const submitTaskProblem = (taskId, problemId, files, ruleIndices) => {
    const payload = Array.isArray(files) ? files : [files]
    const formData = new FormData()
    payload.forEach(file => {
        if (file) formData.append('files', file)
    })
    if (Array.isArray(ruleIndices)) {
        ruleIndices.forEach(idx => {
            formData.append('ruleIndices', idx)
        })
    }
    return apiClient.post(`/tasks/${taskId}/problems/${problemId}/submit`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000
    })
}

// ============ 判题 API ============

// 获取任务提交列表（用于判题）
export const getTaskSubmissions = (taskId) => {
    return apiClient.get(`/tasks/${taskId}/submissions`)
}

// 下载任务下全部学生提交答案（zip）
export const downloadTaskSubmissionAnswers = (taskId) => {
    return apiClient.get(`/tasks/${taskId}/submissions/download`, {
        responseType: 'blob',
        timeout: 300000
    })
}

export const getTaskSubmissionAnswersDownloadUrl = (taskId) => {
    const baseURL = String(apiClient.defaults.baseURL || '/api').replace(/\/$/, '')
    return `${baseURL}/tasks/${taskId}/submissions/download`
}

// 获取任务提交文件下载链接（教师/管理员/提交学生本人）
export const getTaskSubmissionFileUrl = (taskId, submissionId, objectKey) => {
    return apiClient.get(`/tasks/${taskId}/submissions/${submissionId}/files/url`, {
        params: { objectKey }
    })
}

// 触发单个提交判题（异步）
export const gradeSubmission = (taskId, submissionId, scoringType = null) => {
    const body = scoringType ? { scoringType } : {}
    return apiClient.post(`/tasks/${taskId}/submissions/${submissionId}/grade`, body)
}

// 触发批量判题（异步）
export const gradeAllSubmissions = (taskId, overwrite = false, scoringType = null) => {
    const body = { overwrite }
    if (scoringType) body.scoringType = scoringType
    return apiClient.post(`/tasks/${taskId}/grade-all`, body)
}

// 触发题目判题（异步）
export const gradeProblemSubmissions = (
    taskId,
    problemId,
    overwrite = false,
    scoringType = null,
    highPriority = false
) => {
    const body = { overwrite }
    if (scoringType) body.scoringType = scoringType
    if (highPriority) body.highPriority = true
    return apiClient.post(`/tasks/${taskId}/problems/${problemId}/grade`, body)
}

export const getProblemGradingChargePreview = (taskId, problemId) => {
    return apiClient.get(`/tasks/${taskId}/problems/${problemId}/grading-charge-preview`)
}

// 获取判题任务列表
export const getGradingJobs = (taskId) => {
    return apiClient.get(`/tasks/${taskId}/grading-jobs`)
}

// ============ 题目管理 API ============

// 获取题目列表
export const getProblems = (page = 0, size = 20, filters = {}) => {
    const keyword = typeof filters?.keyword === 'string' ? filters.keyword.trim() : ''
    const normalizedStatus = String(filters?.status || '').trim().toUpperCase()
    const isPublicParam = filters?.isPublic === true ? true : (filters?.isPublic === false ? false : undefined)
    return apiClient.get('/problems', {
        params: {
            page,
            size,
            keyword: keyword || undefined,
            type: filters?.type || undefined,
            difficulty: filters?.difficulty || undefined,
            status: normalizedStatus || undefined,
            isPublic: isPublicParam
        }
    })
}

// 获取题目详情
export const getProblemDetail = (problemId) => {
    return apiClient.get(`/problems/${problemId}`)
}

// 创建题目
export const createProblem = (data) => {
    return apiClient.post('/problems', data)
}

// 更新题目
export const updateProblem = (problemId, data) => {
    return apiClient.post(`/problems/${problemId}`, data)
}

// 删除题目（教师删除自己创建的题目）
export const deleteProblem = (problemId) => {
    return apiClient.delete(`/problems/${problemId}`)
}

// 学生参与题目
export const participateProblem = (problemId) => {
    return apiClient.post(`/problems/${problemId}/participate`)
}

// 上传题目文件
export const uploadProblemFile = (problemId, file, options = {}) => {
    const formData = new FormData()
    formData.append('file', file)
    return apiClient.post(`/problems/${problemId}/files`, formData, {
        adapter: 'xhr',
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: Number.isFinite(Number(options.timeout)) ? Number(options.timeout) : 300000,
        onUploadProgress: typeof options.onUploadProgress === 'function' ? options.onUploadProgress : undefined
    })
}

export const uploadProblemTempFile = (problemId, file, options = {}) => {
    const formData = new FormData()
    formData.append('file', file)
    return apiClient.post(`/problems/${problemId}/files/temp`, formData, {
        adapter: 'xhr',
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: Number.isFinite(Number(options.timeout)) ? Number(options.timeout) : 300000,
        onUploadProgress: typeof options.onUploadProgress === 'function' ? options.onUploadProgress : undefined
    })
}

export const uploadProblemCreateTempFile = (file, allowedExtensions, options = {}) => {
    const formData = new FormData()
    formData.append('file', file)
    return apiClient.post('/problems/files/temp', formData, {
        adapter: 'xhr',
        headers: { 'Content-Type': 'multipart/form-data' },
        params: {
            allowedExtensions: allowedExtensions || undefined
        },
        timeout: Number.isFinite(Number(options.timeout)) ? Number(options.timeout) : 300000,
        onUploadProgress: typeof options.onUploadProgress === 'function' ? options.onUploadProgress : undefined
    })
}

// 删除题目文件
export const deleteProblemFile = (problemId, objectKey) => {
    return apiClient.delete(`/problems/${problemId}/files`, { params: { objectKey } })
}

export const deleteProblemTempFile = (problemId, objectKey) => {
    return apiClient.delete(`/problems/${problemId}/files/temp`, { params: { objectKey } })
}

export const deleteProblemCreateTempFile = (objectKey) => {
    return apiClient.delete('/problems/files/temp', { params: { objectKey } })
}

// 获取文件下载链接
export const getProblemFileUrl = (problemId, objectKey) => {
    return apiClient.get(`/problems/${problemId}/files/url`, { params: { objectKey } })
}

export const getTaskProblemFileUrl = (taskId, problemId, objectKey) => {
    return apiClient.get(`/tasks/${taskId}/problems/${problemId}/files/url`, { params: { objectKey } })
}

// 下载题目全部附件（zip）
export const downloadProblemFiles = (problemId) => {
    return apiClient.get(`/problems/${problemId}/files/download`, {
        responseType: 'blob',
        timeout: 300000
    })
}

// ============ 内部附件 API ============
export const uploadInternalCreateTempFile = (file, options = {}) => {
    const formData = new FormData()
    formData.append('file', file)
    return apiClient.post('/problems/internal-files/temp', formData, {
        adapter: 'xhr',
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: Number.isFinite(Number(options.timeout)) ? Number(options.timeout) : 300000,
        onUploadProgress: typeof options.onUploadProgress === 'function' ? options.onUploadProgress : undefined
    })
}

export const uploadInternalTempFile = (problemId, file, options = {}) => {
    const formData = new FormData()
    formData.append('file', file)
    return apiClient.post(`/problems/${problemId}/internal-files/temp`, formData, {
        adapter: 'xhr',
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: Number.isFinite(Number(options.timeout)) ? Number(options.timeout) : 300000,
        onUploadProgress: typeof options.onUploadProgress === 'function' ? options.onUploadProgress : undefined
    })
}

export const deleteInternalCreateTempFile = (objectKey) => {
    return apiClient.delete('/problems/internal-files/temp', { params: { objectKey } })
}

export const deleteInternalTempFile = (problemId, objectKey) => {
    return apiClient.delete(`/problems/${problemId}/internal-files/temp`, { params: { objectKey } })
}

export const deleteInternalFile = (problemId, objectKey) => {
    return apiClient.delete(`/problems/${problemId}/internal-files`, { params: { objectKey } })
}

export const getInternalFileUrl = (problemId, objectKey) => {
    return apiClient.get(`/problems/${problemId}/internal-files/url`, { params: { objectKey } })
}

export const downloadInternalFiles = (problemId) => {
    return apiClient.get(`/problems/${problemId}/internal-files/download`, {
        responseType: 'blob',
        timeout: 300000
    })
}

// ============ 系统配置（公共）API ============
export const getContestEnabled = () => {
    if (USE_MOCK) {
        const settings = JSON.parse(localStorage.getItem('mockSystemSettings') || '{}')
        return Promise.resolve({ code: 200, data: { contestEnabled: !!settings.contestEnabled } })
    }
    return apiClient.get('/system/contest-enabled')
}

// 获取测试大赛开关（管理员）
export const getAdminContestEnabled = () => {
    if (USE_MOCK) {
        const settings = JSON.parse(localStorage.getItem('mockSystemSettings') || '{}')
        return Promise.resolve({ code: 200, data: { contestEnabled: !!settings.contestEnabled } })
    }
    return apiClient.get('/admin/system/contest-enabled')
}

// 设置测试大赛开关（管理员）
export const setAdminContestEnabled = (contestEnabled) => {
    if (USE_MOCK) {
        const raw = localStorage.getItem('mockSystemSettings')
        const settings = raw ? JSON.parse(raw) : {}
        settings.contestEnabled = !!contestEnabled
        localStorage.setItem('mockSystemSettings', JSON.stringify(settings))
        return Promise.resolve({ code: 200, message: contestEnabled ? '测试大赛已开启' : '测试大赛已关闭', data: { contestEnabled: settings.contestEnabled } })
    }
    return apiClient.post('/admin/system/contest-enabled', { contestEnabled })
}

// 获取首页公开视频签名播放链接
export const getPublicVideoUrl = (type) => {
    return apiClient.get(`/public/videos/${encodeURIComponent(type)}/url`)
}

// ============ GitCode登录 API ============
export const getGitcodeAuthUrl = () => {
    return apiClient.get('/auth/gitcode/url')
}

export const gitcodeLoginCallback = (code, role) => {
    return apiClient.post('/auth/gitcode/callback', { code, role })
}

export const gitcodeBind = (code) => {
    return apiClient.post('/auth/gitcode/bind', { code })
}

export const gitcodeUnbind = () => {
    return apiClient.post('/auth/gitcode/unbind')
}

// ============ 微信登录 API ============
export const getWechatAuthUrl = () => {
    return apiClient.get('/auth/wechat/url')
}

export const wechatLoginCallback = (code, role) => {
    return apiClient.post('/auth/wechat/callback', { code, role })
}

export const wechatBind = (code) => {
    return apiClient.post('/auth/wechat/bind', { code })
}

export const wechatUnbind = () => {
    return apiClient.post('/auth/wechat/unbind')
}

// 退出登录
export const logout = () => {
    localStorage.removeItem('token')
    localStorage.removeItem('currentUser')
    window.location.href = '/login'
}
// 获取用户角色对应的首页路径
export const getHomePathByRole = (role) => {
    switch (role) {
        case USER_ROLES.ADMIN:
            return '/admin/profile'
        case USER_ROLES.TEACHER:
            return '/teacher/announcement'
        case USER_ROLES.STUDENT:
            return '/student/announcement'
        default:
            return '/'
    }
}

// ============ 提交文件要求模板 API ============
export const getSubmissionRuleTemplates = () => {
    return apiClient.get('/submission-rule-templates')
}

export const createSubmissionRuleTemplate = (data) => {
    return apiClient.post('/submission-rule-templates', data)
}

export const updateSubmissionRuleTemplate = (id, data) => {
    return apiClient.put(`/submission-rule-templates/${id}`, data)
}

export const deleteSubmissionRuleTemplate = (id) => {
    return apiClient.delete(`/submission-rule-templates/${id}`)
}

// ============ 前端直传 OSS API（presign -> PUT直传 -> confirm）============

// --- 上传限制 ---
export const getUploadLimits = () => {
    return apiClient.get('/system/upload-limits')
}

// --- 头像直传 ---
export const uploadUserAvatarDirect = async (file) => {
    const presignRes = await apiClient.post('/users/me/avatar/presign', {
        filename: file.name, fileSize: file.size, contentType: file.type
    })
    const { objectKey, uploadUrl, contentType } = presignRes.data
    await ossDirectUpload(uploadUrl, file, contentType)
    return apiClient.post('/users/me/avatar/confirm', { objectKey, fileSize: file.size })
}

// --- 题目附件直传 ---
export const uploadProblemFileDirect = async (problemId, file, options = {}) => {
    const presignRes = await apiClient.post(`/problems/${problemId}/files/presign`, {
        filename: file.name, fileSize: file.size, contentType: file.type
    })
    const { objectKey, uploadUrl, contentType } = presignRes.data
    await ossDirectUpload(uploadUrl, file, contentType, options)
    return apiClient.post(`/problems/${problemId}/files/confirm`, { objectKey, fileSize: file.size })
}

export const uploadProblemTempFileDirect = async (problemId, file, options = {}) => {
    const presignRes = await apiClient.post(`/problems/${problemId}/files/temp/presign`, {
        filename: file.name, fileSize: file.size, contentType: file.type
    })
    const { objectKey, uploadUrl, contentType } = presignRes.data
    await ossDirectUpload(uploadUrl, file, contentType, options)
    return apiClient.post(`/problems/${problemId}/files/temp/confirm`, { objectKey, fileSize: file.size })
}

export const uploadProblemCreateTempFileDirect = async (file, allowedExtensions, options = {}) => {
    const presignRes = await apiClient.post('/problems/files/temp/presign', {
        filename: file.name, fileSize: file.size, contentType: file.type
    }, {
        params: { allowedExtensions: allowedExtensions || undefined }
    })
    const { objectKey, uploadUrl, contentType } = presignRes.data
    await ossDirectUpload(uploadUrl, file, contentType, options)
    return apiClient.post('/problems/files/temp/confirm', { objectKey, fileSize: file.size })
}

// --- 内部附件直传 ---
export const uploadInternalCreateTempFileDirect = async (file, options = {}) => {
    const presignRes = await apiClient.post('/problems/internal-files/temp/presign', {
        filename: file.name, fileSize: file.size, contentType: file.type
    })
    const { objectKey, uploadUrl, contentType } = presignRes.data
    await ossDirectUpload(uploadUrl, file, contentType, options)
    return apiClient.post('/problems/internal-files/temp/confirm', { objectKey, fileSize: file.size })
}

export const uploadInternalTempFileDirect = async (problemId, file, options = {}) => {
    const presignRes = await apiClient.post(`/problems/${problemId}/internal-files/temp/presign`, {
        filename: file.name, fileSize: file.size, contentType: file.type
    })
    const { objectKey, uploadUrl, contentType } = presignRes.data
    await ossDirectUpload(uploadUrl, file, contentType, options)
    return apiClient.post(`/problems/${problemId}/internal-files/temp/confirm`, { objectKey, fileSize: file.size })
}

// --- 作业提交直传 ---
export const submitTaskProblemDirect = async (taskId, problemId, files, ruleIndices) => {
    const payload = Array.isArray(files) ? files : [files]
    const presignItems = payload.map((file, i) => ({
        filename: file.name,
        fileSize: file.size,
        contentType: file.type,
        ruleIndex: ruleIndices ? ruleIndices[i] : i
    }))

    const presignRes = await apiClient.post(
        `/tasks/${taskId}/problems/${problemId}/submit/presign`,
        { files: presignItems }
    )

    const uploadPromises = presignRes.data.files.map((presigned, i) =>
        ossDirectUpload(presigned.uploadUrl, payload[i], presigned.contentType)
    )
    await Promise.all(uploadPromises)

    const confirmItems = presignRes.data.files.map((p, i) => ({
        objectKey: p.objectKey,
        fileSize: presignItems[i].fileSize,
        filename: presignItems[i].filename,
        ruleIndex: p.ruleIndex
    }))
    return apiClient.post(
        `/tasks/${taskId}/problems/${problemId}/submit/confirm`,
        { files: confirmItems }
    )
}

// --- 竞赛提交直传 ---
export const submitContestProblemDirect = async (contestId, problemId, files) => {
    const payload = Array.isArray(files) ? files : [files]
    const presignItems = payload.map((file, i) => ({
        filename: file.name,
        fileSize: file.size,
        contentType: file.type,
        ruleIndex: i
    }))

    const presignRes = await apiClient.post(
        `/contests/${contestId}/problems/${problemId}/submit/presign`,
        { files: presignItems }
    )

    const uploadPromises = presignRes.data.files.map((presigned, i) =>
        ossDirectUpload(presigned.uploadUrl, payload[i], presigned.contentType)
    )
    await Promise.all(uploadPromises)

    const confirmItems = presignRes.data.files.map((p, i) => ({
        objectKey: p.objectKey,
        fileSize: presignItems[i].fileSize,
        filename: presignItems[i].filename,
        ruleIndex: p.ruleIndex
    }))
    return apiClient.post(
        `/contests/${contestId}/problems/${problemId}/submit/confirm`,
        { files: confirmItems }
    )
}

// --- 模板直传 ---
export const uploadStudentTemplateDirect = async (file) => {
    const presignRes = await apiClient.post('/templates/student-import/presign', {
        filename: file.name, fileSize: file.size, contentType: file.type
    })
    const { objectKey, uploadUrl, contentType } = presignRes.data
    await ossDirectUpload(uploadUrl, file, contentType)
    return apiClient.post('/templates/student-import/confirm', { objectKey, fileSize: file.size })
}

export const uploadTeacherCertificateTemplateDirect = async (file) => {
    const presignRes = await apiClient.post('/templates/teacher-certificate/presign', {
        filename: file.name, fileSize: file.size, contentType: file.type
    })
    const { objectKey, uploadUrl, contentType } = presignRes.data
    await ossDirectUpload(uploadUrl, file, contentType)
    return apiClient.post('/templates/teacher-certificate/confirm', { objectKey, fileSize: file.size })
}

export default apiClient



